import type { activityLog, alerts, approvals, audience, brand, campaignAnalytics, campaigns, canvas, comments, contentBlocks, contentTemplates, debug, deliveryControls, eventExport, exclusions, exports, extras, funnelRetention, imports, links, localization, privacy, pushAnalytics, pushContent, pushSettings, reporting, segments, templating, testSend, transactional, variantStats, webhooks } from '@bb/contracts';
import { serverKeys } from '@bb/contracts';

export type EnvironmentKind = 'dev' | 'qa' | 'staging' | 'prod' | 'custom';

export interface EnvironmentSummary {
  id: string;
  workspaceId: string;
  key: string;
  name: string;
  kind: EnvironmentKind;
  isProduction: boolean;
  /** The signed-in employee's effective permissions in this environment (BB-15). */
  permissions: string[];
  /** Locale of messages' default content (BB-13); the API sends it with the environment. */
  defaultLocale?: string;
  /** Environment settings as stored; the dashboard reads the locales (BB-55). */
  settings?: EnvironmentSettingsView;
}

export type EnvironmentLocale = localization.EnvironmentLocale;
export type TranslationStatus = localization.TranslationStatus;

/** The part of `tenancy.EnvironmentSettings` the dashboard reads; the rest passes through untouched. */
export interface EnvironmentSettingsView {
  /** Locales of messages with the default and fallbacks (BB-55); unset — only `defaultLocale`. */
  locales?: EnvironmentLocale[];
  /** Where a profile's language comes from: `language` or a custom attribute key (BB-55). */
  localeAttribute?: string;
  [key: string]: unknown;
}

/** `PATCH /environments/:id`: settings merge key by key with the stored ones. */
export interface EnvironmentPatch {
  name?: string;
  defaultLocale?: string;
  settings?: Partial<EnvironmentSettingsView>;
}

/** `POST …/translations.csv`: how many cells were applied and the campaign with its updated draft (BB-55). */
export interface TranslationsImport {
  updated: number;
  campaign: campaigns.CampaignView;
}

export interface AppInstanceSummary {
  id: string;
  apiId: string;
  name: string;
  platform: 'android' | 'ios' | 'web';
  identifier: string;
  status: 'active' | 'archived';
}

export interface EmployeeSummary {
  id: string;
  organizationId: string;
  email: string;
  name: string | null;
  status: 'invited' | 'active' | 'deactivated';
  lastSignInAt: string | null;
}

export type GrantScopeType = 'organization' | 'workspace' | 'environment';

export interface GrantSummary {
  id: string;
  kind: 'role' | 'permission';
  role: string | null;
  permission: string | null;
  scopeType: GrantScopeType;
  scopeId: string;
  createdAt: string;
}

export type NewGrant =
  | { kind: 'role'; role: string; scopeType: GrantScopeType; scopeId: string }
  | { kind: 'permission'; permission: string; scopeType: GrantScopeType; scopeId: string };

export interface AccessCatalog {
  roles: Array<{ role: string; permissions: string[] }>;
  permissions: string[];
  specialPermissions: string[];
}

export interface ChangeEntry {
  path: string;
  before?: unknown;
  after?: unknown;
}

export interface AuditEventSummary {
  id: string;
  occurredAt: string;
  actor: { type: 'employee' | 'server_key' | 'system'; id: string | null; email: string | null };
  action: string;
  environmentId: string | null;
  objectType: string | null;
  objectId: string | null;
  summary: ChangeEntry[];
  metadata: Record<string, unknown>;
}

export interface AuditPage {
  items: AuditEventSummary[];
  nextCursor: string | null;
}

export interface AuditFilters {
  environmentId?: string;
  action?: string;
  cursor?: string;
}

export interface ObjectChanges {
  changesSinceLastViewed: number;
  lastEdit: { at: string; actor: AuditEventSummary['actor'] } | null;
  events: AuditEventSummary[];
}

/** Scope catalog from the service contracts (single source of truth). */
export const SERVER_KEY_SCOPES = serverKeys.SERVER_KEY_SCOPES;

export interface ServerKeySummary {
  id: string;
  name: string;
  keyPrefix: string;
  scopes: string[];
  ipAllowlist: string[];
  expiresAt: string | null;
  status: 'active' | 'retiring' | 'retired' | 'expired' | 'revoked';
  retiringUntil: string | null;
  lastUsedAt: string | null;
  lastUsedIp: string | null;
  createdAt: string;
}

export interface NewServerKey {
  name: string;
  scopes: string[];
  ipAllowlist?: string[];
}

/** The plaintext key exists only in this response. */
export interface IssuedServerKey {
  key: string;
  serverKey: ServerKeySummary;
}

export interface EnvironmentIdentifiers {
  environment: { id: string; key: string; name: string };
  apps: Array<{ id: string; apiId: string; name: string; platform: 'android' | 'ios' | 'web'; identifier: string }>;
  serverApi: { baseUrl: string; keyFormat: string; docs: string };
}

export interface SdkKeySummary {
  id: string;
  keyPrefix: string;
  status: 'active' | 'retiring' | 'retired' | 'expired' | 'revoked';
  retiringUntil: string | null;
  lastUsedAt: string | null;
  createdAt: string;
}

export interface AppDetails {
  app: {
    id: string;
    apiId: string;
    name: string;
    platform: 'android' | 'ios' | 'web';
    identifier: string;
    status: 'active' | 'archived';
    uninstallTrackingEnabled: boolean;
    minSupportedSdkVersion: string | null;
  };
  sdkEndpoint: string;
  latestSdkVersion: string | null;
  sdkKeys: SdkKeySummary[];
  sdkVersions: Array<{
    sdkVersion: string;
    appVersion: string;
    lastSeenAt: string;
    installationsEstimate: number | null;
    marker: 'unsupported' | 'update_available' | 'current';
  }>;
}

export interface AppSettingsPatch {
  name?: string;
  uninstallTrackingEnabled?: boolean;
  minSupportedSdkVersion?: string | null;
}

export interface InternalGroupSummary {
  id: string;
  name: string;
  description: string | null;
  isContentTest: boolean;
  isSeed: boolean;
  isEventLog: boolean;
  memberCount: number;
}

export interface NewInternalGroup {
  name: string;
  description?: string | null;
  isContentTest?: boolean;
  isSeed?: boolean;
  isEventLog?: boolean;
}

export interface GroupMember {
  profileId: string;
  externalId: string | null;
  emailMasked: string | null;
  email?: string | null;
  addedAt: string;
}

export type MemberRef = { externalId: string } | { email: string } | { profileId: string };

export interface TagRef {
  id: string;
  name: string;
  color: string | null;
}

/** A tag with how many objects of each type use it (Settings → Tags). */
export interface TagSummary extends TagRef {
  usage: Record<string, number>;
  total: number;
}

export type DataClass = 'raw_events' | 'interaction_facts' | 'message_payloads' | 'audit' | 'sdk_debug_logs';

export interface RetentionPolicy {
  dataClass: DataClass;
  retainDays: number;
  archiveMode: 'delete' | 'cold_storage';
  dryRunPending: boolean;
  updatedAt: string;
  lastRun: { status: 'dry_run' | 'completed' | 'failed'; startedAt: string; rowsAffected: number; cutoff: string } | null;
}

export type SubscriptionChannel = 'email' | 'push' | 'sms' | 'whatsapp';

export interface SubscriptionGroupSummary {
  id: string;
  apiId: string;
  name: string;
  channel: SubscriptionChannel;
  purpose: 'marketing' | 'notifications' | 'subscriptions';
  topic: string;
  description: string | null;
  status: 'active' | 'archived';
  defaultState: 'subscribed' | 'unsubscribed';
  allowClientUpdates: boolean;
  archivedAt: string | null;
  subscribedCount?: number | null;
  unsubscribedCount?: number | null;
  statsDay?: string | null;
}

export interface NewSubscriptionGroup {
  name: string;
  channel: SubscriptionChannel;
  purpose: SubscriptionGroupSummary['purpose'];
  topic: string;
  defaultState?: 'subscribed' | 'unsubscribed';
  allowClientUpdates?: boolean;
}

export interface Dependent {
  objectType: string;
  objectId: string;
  name: string;
  via: string;
}

export type EventStatus = 'unreviewed' | 'active' | 'deprecated' | 'blocked' | 'test_only';
export type EventProducer = 'sdk' | 'server' | 'both' | 'unknown';

export interface EventSummary {
  name: string;
  description: string | null;
  status: EventStatus;
  producer: EventProducer;
  owner: string | null;
  tags: string[];
  volume30d: number;
  lastSeenAt: string | null;
  propertyCount: number;
}

export interface EventProperty {
  path: string;
  dataType: string;
  sensitivity: 'none' | 'pii' | 'secret';
  segmentable?: boolean;
  description: string | null;
}

export interface EventDetails {
  name: string;
  description: string | null;
  owner: string | null;
  producer: EventProducer;
  status: EventStatus;
  deprecationNote: string | null;
  replacedBy: string | null;
  lastSeenAt: string | null;
  tags: string[];
  properties: EventProperty[];
  examplePayload: Record<string, unknown> | null;
  daily: Array<{ day: string; count: number }>;
  usage: Dependent[];
  aliases?: string[];
  notices: Array<{ id: string; kind: string; subject: string; count: number }>;
}

export interface EventPickerItem {
  name: string;
  description: string | null;
  status: EventStatus;
  deprecated: boolean;
  properties: Array<{ path: string; dataType: string; segmentable?: boolean }>;
  examplePayload: Record<string, unknown> | null;
}

export type AttributeStatus = 'unreviewed' | 'active' | 'deprecated' | 'blocked';
export type AttributeProducer = 'sdk' | 'server' | 'both' | 'import' | 'unknown';
export type PiiClass = 'none' | 'pii';

export interface AttributeSummary {
  key: string;
  kind: 'standard' | 'custom';
  dataType: string;
  status: AttributeStatus;
  description: string | null;
  owner: string | null;
  producer: AttributeProducer;
  piiClass: PiiClass;
  piiClassificationSource: 'default' | 'known_key' | 'heuristic' | 'manual';
  piiReviewRequired: boolean;
  lastSeenAt: string | null;
  conflicts7d: number;
  tags?: string[];
  /** Who may write it (BB-33); standard attributes are always `any`. */
  writePolicy?: WritePolicy;
}

export interface AttributeDetails extends AttributeSummary {
  aliases?: string[];
  deprecationNote: string | null;
  replacedBy: string | null;
  tags: string[];
  conflicts: Array<{ expectedType: string; receivedType: string; sourceKind: string; count: number; lastSeenAt: string; sample: string | null }>;
  usage: Dependent[];
  profilesWithValue: { count: number; capped: boolean };
  topValues: Array<{ value: unknown; count: number }> | null;
  topValuesHidden: 'pii' | 'data_type' | null;
}

export type WritePolicy = 'any' | 'server_only_report' | 'server_only';

export type AttributePatch = Partial<Pick<AttributeDetails, 'description' | 'owner' | 'producer' | 'status' | 'piiClass' | 'deprecationNote' | 'replacedBy'>> & { writePolicy?: WritePolicy; reason?: string };

export interface TrustCounts {
  reported: number;
  rejected: number;
}

export interface TrustItem {
  kind: 'attribute' | 'event';
  name: string;
  writePolicy: WritePolicy;
  writePolicyChangedAt: string | null;
  violations: TrustCounts;
  bySource: Array<{ sourceKind: string } & TrustCounts>;
  byAppVersion: Array<{ appInstanceId: string; appVersion: string; sdkVersion: string } & TrustCounts>;
  readyToEnforce: boolean;
}

export type MediaMime = 'image/png' | 'image/jpeg' | 'image/gif' | 'image/webp';
export type MediaPreset = 'thumb' | 'card_banner_2_96' | 'push_1_1' | 'push_2_1' | 'iam_full';
export interface MediaCrop {
  x: number;
  y: number;
  width: number;
  height: number;
}

export interface MediaRenditionView {
  status: 'pending' | 'ready' | 'skipped' | 'failed';
  crop: MediaCrop;
  width: number | null;
  height: number | null;
  url: string | null;
  error: string | null;
}

export interface MediaVersionView {
  id: string;
  version: number;
  mime: MediaMime;
  bytes: number;
  width: number;
  height: number;
  animated: boolean;
  originalFileName: string | null;
  createdAt: string;
  url: string;
  renditions: Partial<Record<MediaPreset, MediaRenditionView>>;
  author?: string;
}

export interface MediaAssetSummary {
  id: string;
  name: string;
  altText: string | null;
  currentVersion: number;
  author: string;
  createdAt: string;
  archivedAt: string | null;
  tags: string[];
  current: MediaVersionView;
}

export interface MediaAssetDetails extends Omit<MediaAssetSummary, 'current'> {
  versions: MediaVersionView[];
  usage: Dependent[];
}

/** What content stores for an image (BB-52): a fixed version and, optionally, a channel rendition. */
export interface MediaRef {
  assetId: string;
  versionId: string;
  rendition?: MediaPreset;
}

export interface NameAlias {
  id: string;
  kind: 'event' | 'attribute';
  aliasName: string;
  canonicalName: string;
  source: 'alias_map' | 'admin';
  note: string | null;
  createdAt: string;
  usage30d: number;
  lastSeenDay: string | null;
}

export interface AliasUsageGroup {
  kind: 'event' | 'attribute';
  canonicalName: string;
  total: number | null;
  aliases: Array<{ aliasName: string; count: number; share: number | null; byAppVersion: Array<{ appVersion: string; count: number }> }>;
}

export interface FilterCatalogEntry {
  type: string;
  category: string;
  label: { uk: string; en: string };
  valueType: 'string' | 'number' | 'boolean' | 'time' | 'array' | 'version' | 'flag' | 'push_states' | 'group_state' | 'membership' | 'event_flag' | 'event_time' | 'count';
  operators: string[];
  params: Array<{
    name: string;
    kind: 'attribute' | 'data_type' | 'profile_field' | 'app' | 'days' | 'platform' | 'device_scope' | 'subscription_group' | 'segment' | 'event' | 'event_window' | 'event_property' | 'time_unit' | 'message_target' | 'campaign_or_canvas' | 'canvas' | 'variant' | 'channel' | 'within_days' | 'attribution_field';
    required: boolean;
  }>;
  template: Record<string, unknown>;
}

export interface FilterCatalog {
  categories: Array<{ key: string; label: { uk: string; en: string } }>;
  operatorsByType: Record<string, string[]>;
  types: FilterCatalogEntry[];
}

export type AudienceReach = audience.AudienceReach;
export type AudienceExactRequest = audience.AudienceExactRequest;
export type AudienceLookup = audience.AudienceLookup;
export type SegmentSummary = segments.SegmentSummary;
export type SegmentView = segments.SegmentView;
export type SegmentRevisionView = segments.SegmentRevisionView;
export type SegmentCampaignDraft = segments.SegmentCampaignDraft;
export type SegmentSizePoint = segments.SegmentSizePoint;
export type SegmentInsights = segments.SegmentInsights;
export type DependentView = segments.DependentView;
export type WhereUsed = segments.WhereUsed;
export type SuppressionListView = exclusions.SuppressionListView;
export type ExportView = exports.ExportView;
export type ExportColumnOption = exports.ExportColumnOption;
export type GlobalControlView = exclusions.GlobalControlView;
export type DeliveryControls = deliveryControls.DeliveryControls;
export type MessageExemptions = deliveryControls.MessageExemptions;
export type FrequencyCapRuleView = deliveryControls.FrequencyCapRuleView;
export type FrequencyCapRuleBody = deliveryControls.FrequencyCapRuleBody;
export type UpdateFrequencyCapRuleBody = deliveryControls.UpdateFrequencyCapRuleBody;
export type RateLimitsView = deliveryControls.RateLimitsView;
export type PutRateLimitsBody = deliveryControls.PutRateLimitsBody;
export type DebugLogSummary = debug.DebugLogSummary;
export type DebugLogEntry = debug.DebugLogEntry;
export type RejectionRow = debug.RejectionRow;
export type DebugSessionView = debug.DebugSessionView;
export type SdkLogLineView = debug.SdkLogLineView;
export interface EventLogFilters {
  after?: string;
  surface?: 'sdk' | 'server';
  externalId?: string;
  installationId?: string;
  code?: string;
  sessionId?: string;
}
export type SegmentArchiveResult = segments.SegmentArchiveResult;
export type ExtrasSchemaView = extras.ExtrasSchemaView;
export type ExtraDefinitionView = extras.ExtraDefinitionView;
export type ExtrasCheck = extras.ExtrasCheck;
export type ExtrasChannel = extras.ExtrasChannel;
export type ExtraType = extras.ExtraType;
export interface ExtraDefinitionInput {
  type: ExtraType;
  enumValues: string[];
  required: boolean;
  description: string | null;
  deprecated: boolean;
  appliesWhen: { key: string; in: string[] } | null;
}
export type DeeplinkRouteView = links.DeeplinkRouteView;
export type DeeplinkValidation = links.DeeplinkValidation;
export type DeeplinkInput = Omit<links.DeeplinkRouteView, 'id' | 'updatedAt'>;
export type CommentThreadView = comments.CommentThreadView;
export type CommentThreadList = comments.CommentThreadList;
export type CommentPerson = comments.CommentPerson;
export type CommentObjectType = comments.CommentObjectType;
export type ApprovalPolicy = approvals.ApprovalPolicy;
export type ApprovalPolicyView = approvals.ApprovalPolicyView;
export type ApprovalRequestView = approvals.ApprovalRequestView;
export type ApprovalState = approvals.ApprovalState;
export type MessagingRule = approvals.MessagingRule;
export type BrandThemeView = brand.BrandThemeView;
export type BrandTokens = brand.BrandTokens;
export type A11ySettings = brand.A11ySettings;
export type A11yCheck = brand.A11yCheck;
export type PrivacyRequestView = privacy.PrivacyRequestView;
export interface MergePreview {
  source: { profileId: string; externalId: string | null };
  target: { profileId: string; externalId: string | null };
  moves: Record<string, number>;
  attributeConflicts: string[];
  attributesFilled: string[];
  subscriptionChanges: Array<{ kind: 'channel' | 'group'; name: string; from: string | null; to: string }>;
  journeysEnded: number;
  mergeId?: string;
  status?: 'pending' | 'completed' | 'failed';
}
export type TombstoneView = privacy.TombstoneView;
export type EventExportConnectorView = eventExport.ConnectorView;
export type EventExportConnectorStatus = eventExport.ConnectorStatus;
export type EventExportS3Config = eventExport.S3Config;
export type CreateEventExportConnectorBody = eventExport.CreateConnectorBody;
export type UpdateEventExportConnectorBody = eventExport.UpdateConnectorBody;
export type EventExportTestResult = eventExport.TestConnectionResult;
export type EventExportWindowView = eventExport.WindowView;
export type EventExportWindowsPage = eventExport.WindowsPage;
export type EventExportReconcileView = eventExport.ReconcileView;
export type EventExportReconcileStatus = eventExport.ReconcileStatus;
export type ImportView = imports.ImportView;
export type ImportSummary = imports.ImportSummary;
export type ImportColumnMapping = imports.ColumnMapping;
export type ImportOptions = imports.ImportOptions;
export type HomeView = reporting.HomeView;
export type ChannelDashboardView = reporting.ChannelDashboardView;
export type DashboardChannel = ChannelDashboardView['channel'];
export interface DashboardFilters {
  days?: number;
  platform?: 'android' | 'ios' | 'web';
  category?: 'marketing' | 'transactional';
  compareBy?: ChannelDashboardView['compareBy'];
}
export type HomeKpi = reporting.HomeKpi;
export type HomeDay = reporting.HomeDay;
export type RecentObjectView = reporting.RecentObjectView;
/** Canvas analytics by step (BB-123): `GET …/canvases/:id/analytics?from&to[&versionId][&variantId]`. */
export type CanvasAnalyticsQuery = canvas.CanvasAnalyticsQuery;
export type CanvasAnalytics = canvas.CanvasAnalytics;
export type CanvasStepStats = canvas.CanvasStepStats;
export type CanvasStuckEntry = canvas.CanvasStuckEntry;
/** Push analytics per delivery (BB-79); `groupBy` defaults to `platform`, `basis` to `send_cohort` on the server. */
export type PushAnalyticsFilters = Pick<pushAnalytics.PushAnalyticsQuery, 'from' | 'to'> & Partial<Omit<pushAnalytics.PushAnalyticsQuery, 'from' | 'to'>>;
export type PushAnalyticsResponse = pushAnalytics.PushAnalyticsResponse;
export type PushAnalyticsRow = pushAnalytics.PushAnalyticsRow;
export type PushMetricDefinition = pushAnalytics.PushMetricDefinition;
export type PushMetricKey = pushAnalytics.PushMetricKey;
export type PushSuppressedResponse = pushAnalytics.PushSuppressedResponse;
/** Campaign analytics (BB-104): `GET …/campaigns/:id/analytics?from&to[&granularity][&breakdown]`; defaults `day`, `channel`. */
export type CampaignAnalyticsQuery = { from: string; to: string; granularity?: campaignAnalytics.CampaignAnalyticsGranularity; breakdown?: campaignAnalytics.CampaignAnalyticsBreakdown };
export type CampaignAnalytics = campaignAnalytics.CampaignAnalytics;
export type CampaignKpi = campaignAnalytics.CampaignKpi;
export type CampaignKpiKey = campaignAnalytics.CampaignKpiKey;
export type CampaignAnalyticsGranularity = campaignAnalytics.CampaignAnalyticsGranularity;
export type CampaignAnalyticsBreakdown = campaignAnalytics.CampaignAnalyticsBreakdown;
export type CampaignCounts = campaignAnalytics.CampaignCounts;
export type CampaignChannelRow = campaignAnalytics.CampaignChannelRow;
export type ChannelMetricDefinition = campaignAnalytics.ChannelMetricDefinition;
/** Variant and control statistics (BB-136); the server defaults `basis=assigned`, `mde=0.1`, the primary conversion and the whole history. */
export type VariantStatsQuery = { conversionId?: string; basis?: variantStats.VariantStatsBasis; mde?: number; from?: string; to?: string };
export type VariantStatsView = variantStats.VariantStatsView;
export type VariantStatsRow = variantStats.VariantStatsRow;
export type VariantStatsBasis = variantStats.VariantStatsBasis;
/** Funnel and retention reports of a campaign or Canvas (BB-135); 422 `report_timeout` when the query is too heavy. */
export type FunnelRequest = funnelRetention.FunnelRequest;
export type FunnelReport = funnelRetention.FunnelReport;
export type FunnelStep = funnelRetention.FunnelStep;
export type FunnelStepResult = funnelRetention.FunnelStepResult;
export type RetentionRequest = funnelRetention.RetentionRequest;
export type RetentionReport = funnelRetention.RetentionReport;
export type RetentionRow = funnelRetention.RetentionRow;
export type ReportSource = funnelRetention.ReportSource;
export type ReportStart = funnelRetention.ReportStart;
/** Message Activity Log (BB-105): newest first, cursor paging; `isTest` true — test sends only, false — without them. */
export type ActivityLogFilters = Partial<Omit<activityLog.ActivityLogQuery, 'limit'>> & { limit?: number };
export type ActivityLogPage = activityLog.ActivityLogPage;
export type ActivityLogEntry = activityLog.ActivityLogEntry;
export type ActivityLogType = activityLog.ActivityLogType;
export type ActivityLogChannel = activityLog.ActivityLogChannel;
export type AlertRuleView = alerts.AlertRuleView;
export type AlertEventView = alerts.AlertEventView;
export type AlertObjectType = AlertRuleView['objectType'];
export type AlertRuleInput = Omit<AlertRuleView, 'id' | 'state' | 'lastEvaluatedAt' | 'lastValue' | 'createdBy' | 'updatedAt'>;
export interface RevisionDiff {
  from: number;
  to: number;
  changes: Array<{ path: string; before?: unknown; after?: unknown }>;
}
export type CanvasSummary = canvas.CanvasSummary;
export type CanvasView = canvas.CanvasView;
export type CanvasList = canvas.CanvasList;
export type CanvasTemplateView = canvas.CanvasTemplateView;
export type CanvasDefinition = canvas.CanvasDefinition;
export type CanvasDraftView = canvas.CanvasDraftView;
export type CanvasValidation = canvas.CanvasValidation;
export type CanvasValidationIssue = canvas.CanvasValidationIssue;
export type CanvasStepTypeView = canvas.CanvasStepTypeView;
/** `POST …/canvas-steps/delay/preview` (BB-116): `context` defaults to `{}` on the server. */
export type CanvasDelayPreviewBody = { config: unknown; profileId?: string; context?: Record<string, unknown>; at?: string };
export type CanvasDelayPreview = canvas.DelayPreview;
/** `POST …/canvas-steps/context/preview` (BB-119): a Context step run for one profile and an example event; `context` defaults to `{}` on the server. */
export type CanvasContextPreviewBody = { config: unknown; profile?: PreviewProfile; context?: Record<string, unknown>; eventProperties?: Record<string, unknown>; at?: string };
export type CanvasContextPreview = canvas.ContextPreview;
/** Entry, audience, entry controls and exit criteria of the draft (BB-111, BB-112); publish copies them. */
export type CanvasSettings = canvas.CanvasSettings;
export type CanvasEntrySpec = canvas.CanvasEntrySpec;
export type CanvasEntryControls = canvas.EntryControls;
export type CanvasExitEvent = canvas.ExitEvent;
/** `pin` keeps running entries on their version; `migrate` moves them, mapping each removed step to a step or `exit` (BB-110). */
export type CanvasMigration = { mode: 'pin' } | { mode: 'migrate'; map: Record<string, string> };
export type CanvasPublishResult = canvas.CanvasPublishResult;
/** Test Canvas simulator (BB-122): `POST …/canvases/:id/simulations` runs one profile through the saved draft or the running version on a virtual clock. */
/** The request as sent: defaults (`definitionSource`, `entry`, `events`, `deliveryMode`) are the server's. */
export type CanvasSimulationBody = (typeof canvas.SimulationBody)['_zod']['input'];
export type CanvasSimulationEvent = canvas.SimulationEvent;
export type CanvasSimulationProfile = canvas.SimulationProfileSnapshot;
export type CanvasSimulationResult = canvas.SimulationResult;
export type CanvasSimTraceItem = canvas.SimTraceItem;
/** `POST …/resume`: the Canvas and what was rescheduled at once (overdue timers, frozen entries). */
export type CanvasResumeResult = CanvasView & { resumed: { timers: number; entries: number } };
export interface CanvasListFilters {
  query?: string;
  status?: 'draft' | 'active' | 'paused' | 'stopped' | 'archived' | 'idle' | 'all';
  entryType?: NonNullable<CanvasSummary['entryType']>;
  tagId?: string;
  sort?: 'updated' | 'created' | 'name';
  cursor?: string;
}
export type CampaignSummary = campaigns.CampaignSummary;
export type CampaignView = campaigns.CampaignView;
export type CampaignList = campaigns.CampaignList;
export type CampaignRevisionView = campaigns.CampaignRevisionView;
export type CampaignArchiveResult = campaigns.CampaignArchiveResult;
export type CampaignStatus = campaigns.CampaignStatus;
export type CampaignAction = campaigns.CampaignAction;
export type ScheduledDelivery = campaigns.ScheduledDelivery;
export type NextRunView = campaigns.NextRunView;
/** Content calendar (BB-108): runs and send windows of live campaigns and Canvases; days are in `timeZone`, the environment's. */
export type CalendarView = campaigns.CalendarView;
export type CalendarItem = campaigns.CalendarItem;
export type CalendarOverlap = campaigns.CalendarOverlap;
export interface CalendarFilters {
  from: string;
  to: string;
  objectType?: CalendarItem['objectType'];
  channel?: CalendarItem['channels'][number];
  tagId?: string;
  status?: 'active' | 'paused';
}
export type CampaignContent = campaigns.CampaignContent;
export type CampaignDraftIssue = campaigns.CampaignDraftIssue;
export type CampaignDraftValidation = campaigns.CampaignDraftValidation;
export type CampaignWizardStep = campaigns.CampaignWizardStep;
/** `PATCH …/campaigns/:id/draft` (BB-96): only the parts sent change; `draftVersion` guards concurrent edits (412). */
export interface UpdateCampaignDraftInput {
  draftVersion: number;
  name?: string;
  description?: string;
  channels?: CampaignSummary['channels'];
  content?: Partial<CampaignContent>;
}
/** «Next send times» of a schedule being edited (BB-99); times without local time are in `timeZone`, the environment's. */
export interface SchedulePreview {
  items: NextRunView[];
  timeZone: string;
}
/** Personalization of the composer (BB-54): the variables a message may use and the payload one profile would get. */
export type TemplateVariable = templating.TemplateVariable;
export type TemplateVariables = templating.VariablesResponse;
export type PreviewProfile = templating.PreviewProfile;
/** Test send and preview links of the composers (BB-59). */
export type TestSendBody = testSend.TestSendBody;
export type TestSendResponse = testSend.TestSendResponse;
export type TestSendResult = testSend.TestSendResult;
export type TestSendChannel = testSend.TestSendChannel;
export type PreviewLinkChannel = testSend.PreviewLinkChannel;
export type CreatePreviewLinkBody = testSend.CreatePreviewLinkBody;
export type PreviewLinkView = testSend.PreviewLinkView;
export type CreatedPreviewLink = testSend.CreatedPreviewLink;
export type PublicPreview = testSend.PublicPreview;
export type PayloadPreviewBody = templating.PreviewBody;
export type PayloadPreview = templating.PreviewResponse;
/** Outbound webhooks (BB-142): registered endpoints and the domains they may call; a message fills an endpoint's params. */
export type WebhookSettings = webhooks.WebhookSettings;
export type WebhookEndpointView = webhooks.WebhookEndpointView;
export type WebhookEndpointSummary = webhooks.WebhookEndpointSummary;
/** What the form sends: secrets are references (`env:NAME`, `file:/path`), never values. */
export type WebhookEndpointInput = webhooks.CreateWebhookEndpointBody;
export type WebhookEndpointPatch = webhooks.UpdateWebhookEndpointBody;
export type WebhookParam = webhooks.WebhookParam;
export type WebhookChannelContent = webhooks.WebhookChannelContent;
/** Push composer (BB-73, BB-76): the content of `push_android` / `push_ios`, its checks and preview, and the environment's push registry (BB-77). */
export type PushContentInput = pushContent.PushContentInput;
export type PushContentIssue = pushContent.PushContentIssue;
export type PushValidationResult = pushContent.PushValidationResult;
export interface PushValidateRequest {
  content: PushContentInput;
  category?: 'marketing' | 'transactional';
}
export interface PushPreviewRequest {
  content: PushContentInput;
  profile: PreviewProfile;
  eventProperties?: Record<string, unknown>;
  apiTriggerProperties?: Record<string, unknown>;
  context?: Record<string, unknown>;
  locale?: string;
}
export type PushPreviewResult = templating.PushPreviewResponse;
export type PushEnvironmentSettings = pushSettings.PushEnvironmentSettings;
export type AndroidChannelView = pushSettings.AndroidChannelView;
export type IosCategoryView = pushSettings.IosCategoryView;
export type IosThreadView = pushSettings.IosThreadView;
export type AppPushCapabilities = pushSettings.AppPushCapabilities;
/** Push settings page (BB-77): registry writes and the snippet the apps paste in. */
export type AndroidChannelBody = pushSettings.AndroidChannelBody;
export type UpdateAndroidChannelBody = pushSettings.UpdateAndroidChannelBody;
export type IosCategoryBody = pushSettings.IosCategoryBody;
export type IosCategoryButton = pushSettings.IosCategoryButton;
export type IosThreadBody = pushSettings.IosThreadBody;
export type SdkConfigSnippet = pushSettings.SdkConfigSnippet;
/**
 * Push provider credential of an app (BB-72), as `apps/api` `push-credential-service.ts` answers: metadata and health,
 * never the secret — `secretRef` only names where the server reads it (`env:NAME`, `file:/path`).
 */
export type PushCredentialHealth = 'unverified' | 'healthy' | 'failing';
export interface PushCredentialView {
  id: string;
  appId: string;
  provider: 'fcm' | 'apns';
  secretRef: string;
  fcmProjectId: string | null;
  apnsTeamId: string | null;
  apnsKeyId: string | null;
  apnsTopic: string | null;
  apnsEnvironments: string[];
  sendingEnabled: boolean;
  healthStatus: PushCredentialHealth | (string & {});
  lastCheckedAt: string | null;
  lastSuccessAt: string | null;
  lastErrorCode: string | null;
  consecutiveCredentialErrors: number;
  updatedBy: string | null;
  createdAt: string;
  updatedAt: string;
}
export type ApnsEnvironment = 'sandbox' | 'production';
/** `PUT …/push-credential`: creates or replaces; the provider follows the app's platform. */
export type PushCredentialBody =
  | { provider: 'fcm'; secretRef: string; fcmProjectId?: string }
  | { provider: 'apns'; secretRef: string; apnsTeamId: string; apnsKeyId: string; apnsTopic?: string; apnsEnvironments: ApnsEnvironment[] };
export interface PushCredentialCheckView {
  healthStatus: string;
  verdict: 'healthy' | 'credential_error' | 'inconclusive';
  code: string;
  providerCode: string | null;
  latencyMs: number | null;
  detail: string | null;
  checkedAt: string;
}
export interface PushTestSendBody {
  token: string;
  apnsEnvironment?: ApnsEnvironment;
  title: string;
  body: string;
}
export interface PushTestSendView {
  deliveryId: string;
  status: 'accepted' | 'failed' | 'unknown';
  code: string;
  providerCode: string | null;
  httpStatus: number | null;
  latencyMs: number | null;
  providerMessageId: string | null;
}
/** Transactional (trading) campaigns (BB-78). */
export type TransactionalSettings = transactional.TransactionalSettings;
export type TransactionalLatency = transactional.TransactionalLatency;
export interface CampaignListFilters {
  query?: string;
  status?: CampaignStatus | 'idle' | 'all';
  deliveryType?: CampaignSummary['deliveryType'];
  channel?: CampaignSummary['channels'][number];
  tagId?: string;
  sort?: 'updated' | 'created' | 'name';
  cursor?: string;
  limit?: number;
}
export interface CreateCampaignInput {
  name: string;
  description?: string | null;
  deliveryType: CampaignSummary['deliveryType'];
  channels: CampaignSummary['channels'];
}
export interface SegmentListFilters {
  query?: string;
  tagId?: string;
  starred?: boolean;
  status?: 'active' | 'archived' | 'all';
  cursor?: string;
}
export type ExplainedNode = audience.ExplainedNode;

export interface AudienceDescription {
  valid: boolean;
  errors: Array<{ path: string; code: string; message: string }>;
  sentences: string[];
  text: string;
  warnings: Array<{ code: string; nodeId: string; message: string }>;
}

export interface SearchHit {
  objectType: string;
  objectId: string;
  apiId: string | null;
  title: string;
  subtitle: string | null;
  /** Dashboard path of the object. */
  url: string;
  tags: string[];
}

/** Admin API surface used by the dashboard; injected so tests run without a server. */
/** Template library (BB-56). */
export type TemplateChannel = contentTemplates.TemplateChannel;
export type TemplateContent = contentTemplates.TemplateContent;
export type TemplateSource = contentTemplates.TemplateSource;
export type TemplateSummary = contentTemplates.TemplateSummary;
export type TemplateDetails = contentTemplates.TemplateDetails;
export type TemplateVersionView = contentTemplates.TemplateVersionView;
export type TemplateWriteResult = contentTemplates.TemplateWriteResult;
export type TemplateArchiveResult = contentTemplates.TemplateArchiveResult;
export type TemplateInstance = contentTemplates.TemplateInstance;
export type TemplateIssue = contentTemplates.TemplateIssue;
export interface TemplateFilters {
  channel?: TemplateChannel;
  query?: string;
  tag?: string;
  archived?: boolean;
}

/** Content blocks (BB-57). */
export type ContentBlockSummary = contentBlocks.ContentBlockSummary;
export type ContentBlockDetails = contentBlocks.ContentBlockDetails;
export type ContentBlockVersionView = contentBlocks.ContentBlockVersionView;
export type ContentBlockUsageView = contentBlocks.ContentBlockUsageView;
export type ContentBlockArchiveResult = contentBlocks.ContentBlockArchiveResult;
export type ContentBlockIssue = contentBlocks.ContentBlockIssue;
export type ContentBlockPropagateResult = contentBlocks.PropagateResult;
export type ContentBlockKind = contentBlocks.ContentBlockKind;

export interface AdminApi {
  /** The signed-in employee and their organization-level permissions. */
  me(): Promise<{ employee: EmployeeSummary; permissions: string[] }>;
  accessCatalog(): Promise<AccessCatalog>;
  listGrants(employeeId: string): Promise<GrantSummary[]>;
  addGrant(employeeId: string, grant: NewGrant): Promise<GrantSummary>;
  removeGrant(employeeId: string, grantId: string): Promise<void>;
  listAudit(filters: AuditFilters): Promise<AuditPage>;
  objectChanges(environmentId: string, objectType: string, objectId: string): Promise<ObjectChanges>;
  markViewed(environmentId: string, objectType: string, objectId: string): Promise<void>;
  identifiers(environmentId: string): Promise<EnvironmentIdentifiers>;
  listInternalGroups(environmentId: string): Promise<InternalGroupSummary[]>;
  createInternalGroup(environmentId: string, group: NewInternalGroup): Promise<InternalGroupSummary>;
  archiveInternalGroup(environmentId: string, groupId: string): Promise<void>;
  listGroupMembers(environmentId: string, groupId: string): Promise<GroupMember[]>;
  addGroupMember(environmentId: string, groupId: string, member: MemberRef): Promise<{ profileId: string }>;
  removeGroupMember(environmentId: string, groupId: string, profileId: string): Promise<void>;
  appDetails(environmentId: string, appId: string): Promise<AppDetails>;
  updateApp(environmentId: string, appId: string, patch: AppSettingsPatch): Promise<unknown>;
  createSdkKey(environmentId: string, appId: string): Promise<{ key: string; sdkKey: SdkKeySummary }>;
  rotateSdkKey(environmentId: string, appId: string, keyId: string): Promise<{ key: string; sdkKey: SdkKeySummary }>;
  revokeSdkKey(environmentId: string, appId: string, keyId: string): Promise<SdkKeySummary>;
  listServerKeys(environmentId: string): Promise<ServerKeySummary[]>;
  createServerKey(environmentId: string, body: NewServerKey): Promise<IssuedServerKey>;
  rotateServerKey(environmentId: string, keyId: string, overlapHours: number): Promise<IssuedServerKey>;
  revokeServerKey(environmentId: string, keyId: string): Promise<ServerKeySummary>;
  listEnvironments(): Promise<EnvironmentSummary[]>;
  updateEnvironment(environmentId: string, patch: EnvironmentPatch): Promise<unknown>;
  listApps(environmentId: string): Promise<AppInstanceSummary[]>;
  listEmployees(): Promise<EmployeeSummary[]>;
  invite(email: string): Promise<EmployeeSummary>;
  deactivate(employeeId: string): Promise<EmployeeSummary>;
  logout(): Promise<void>;
  listTags(environmentId: string): Promise<TagSummary[]>;
  createTag(environmentId: string, body: { name: string; color?: string | null }): Promise<TagRef>;
  updateTag(environmentId: string, tagId: string, patch: { name?: string; color?: string | null }): Promise<TagRef>;
  deleteTag(environmentId: string, tagId: string, force: boolean): Promise<void>;
  objectTags(environmentId: string, objectType: string, objectId: string): Promise<TagRef[]>;
  setObjectTags(environmentId: string, objectType: string, objectId: string, tagIds: string[]): Promise<TagRef[]>;
  search(environmentId: string, q: string): Promise<SearchHit[]>;
  listRetention(environmentId: string): Promise<RetentionPolicy[]>;
  updateRetention(environmentId: string, policies: Array<{ dataClass: DataClass; retainDays: number }>): Promise<RetentionPolicy[]>;
  listSubscriptionGroups(environmentId: string, filters: { status?: string; channel?: string }): Promise<SubscriptionGroupSummary[]>;
  getSubscriptionGroup(environmentId: string, groupId: string): Promise<SubscriptionGroupSummary>;
  createSubscriptionGroup(environmentId: string, body: NewSubscriptionGroup): Promise<SubscriptionGroupSummary>;
  archiveSubscriptionGroup(environmentId: string, groupId: string): Promise<SubscriptionGroupSummary>;
  subscriptionGroupStats(environmentId: string, groupId: string, days: number): Promise<Array<{ day: string; subscribed: number; unsubscribed: number }>>;
  calculateSubscriptionGroup(environmentId: string, groupId: string): Promise<{ at: string; subscribed: number; unsubscribed: number }>;
  listEvents(environmentId: string, filters: { query?: string; status?: string; producer?: string; cursor?: string }): Promise<{ items: EventSummary[]; nextCursor: string | null; unreviewed: number }>;
  getEvent(environmentId: string, name: string): Promise<EventDetails>;
  createEvent(environmentId: string, body: { name: string; description?: string | null; producer?: EventProducer }): Promise<unknown>;
  updateEvent(
    environmentId: string,
    name: string,
    patch: Partial<Pick<EventDetails, 'description' | 'owner' | 'producer' | 'status' | 'deprecationNote' | 'replacedBy'>> & { writePolicy?: WritePolicy; reason?: string },
  ): Promise<unknown>;
  trustReport(environmentId: string, days: number): Promise<{ days: number; items: TrustItem[] }>;
  updateEventProperty(environmentId: string, name: string, path: string, patch: { description?: string | null; sensitivity?: EventProperty['sensitivity']; segmentable?: boolean }): Promise<unknown>;
  describeAudience(environmentId: string, definition: unknown, locale: 'uk' | 'en'): Promise<AudienceDescription>;
  filterCatalog(environmentId: string): Promise<FilterCatalog>;
  listSegments(environmentId: string, filters: SegmentListFilters): Promise<{ items: SegmentSummary[]; nextCursor: string | null }>;
  getSegment(environmentId: string, segmentId: string): Promise<SegmentView>;
  createSegment(environmentId: string, body: { name: string; description?: string | null; definition: unknown }): Promise<SegmentView>;
  updateSegment(environmentId: string, segmentId: string, body: { revision: number; name?: string; description?: string | null; definition?: unknown }): Promise<SegmentView>;
  archiveSegment(environmentId: string, segmentId: string): Promise<SegmentArchiveResult>;
  whereUsed(environmentId: string, objectType: string, objectId: string): Promise<WhereUsed>;
  restoreSegment(environmentId: string, segmentId: string): Promise<SegmentView>;
  duplicateSegment(environmentId: string, segmentId: string): Promise<SegmentView>;
  starSegment(environmentId: string, segmentId: string, starred: boolean): Promise<{ starred: boolean }>;
  segmentRevisions(environmentId: string, segmentId: string, locale: 'uk' | 'en'): Promise<{ items: SegmentRevisionView[] }>;
  segmentCampaignDraft(environmentId: string, segmentId: string): Promise<SegmentCampaignDraft>;
  segmentHistory(environmentId: string, segmentId: string, days: 30 | 90): Promise<{ items: SegmentSizePoint[] }>;
  segmentInsights(environmentId: string, segmentIds: string[]): Promise<SegmentInsights>;
  extrasSchema(environmentId: string, channel: ExtrasChannel): Promise<ExtrasSchemaView>;
  createExtra(environmentId: string, body: ExtraDefinitionInput & { channel: ExtrasChannel; key: string }): Promise<ExtraDefinitionView>;
  updateExtra(environmentId: string, id: string, body: Partial<ExtraDefinitionInput>): Promise<ExtraDefinitionView>;
  deleteExtra(environmentId: string, id: string): Promise<void>;
  setExtrasStrict(environmentId: string, channel: ExtrasChannel, strict: boolean): Promise<ExtrasSchemaView>;
  validateExtras(environmentId: string, channel: ExtrasChannel, extras: Record<string, string>): Promise<ExtrasCheck>;
  listDeeplinks(environmentId: string, filters: { query?: string; platform?: 'android' | 'ios' | 'both'; includeDeprecated?: boolean }): Promise<{ items: DeeplinkRouteView[] }>;
  validateDeeplink(environmentId: string, uri: string, platform?: 'android' | 'ios'): Promise<DeeplinkValidation>;
  buildDeeplink(environmentId: string, routeId: string, params: Record<string, string>): Promise<{ uri: string; validation: DeeplinkValidation }>;
  createDeeplink(environmentId: string, body: DeeplinkInput): Promise<DeeplinkRouteView>;
  updateDeeplink(environmentId: string, id: string, body: Partial<Omit<DeeplinkInput, 'scheme' | 'host'>>): Promise<DeeplinkRouteView>;
  deleteDeeplink(environmentId: string, id: string): Promise<void>;
  commentThreads(environmentId: string, objectType: CommentObjectType, objectId: string, status: 'open' | 'resolved' | 'all'): Promise<CommentThreadList>;
  createThread(environmentId: string, objectType: CommentObjectType, objectId: string, body: { body: string; mentions: string[]; anchor: { stepId: string } | null }): Promise<CommentThreadView>;
  replyThread(environmentId: string, threadId: string, body: { body: string; mentions: string[] }): Promise<CommentThreadView>;
  setThreadStatus(environmentId: string, threadId: string, action: 'resolve' | 'reopen'): Promise<CommentThreadView>;
  deleteComment(environmentId: string, commentId: string): Promise<CommentThreadView>;
  mentionCandidates(environmentId: string, query: string): Promise<{ items: CommentPerson[] }>;
  approvalPolicy(environmentId: string): Promise<ApprovalPolicyView>;
  setApprovalPolicy(environmentId: string, policy: ApprovalPolicy): Promise<ApprovalPolicyView>;
  requestApproval(environmentId: string, campaignId: string, comment?: string): Promise<ApprovalRequestView>;
  approvalRequests(environmentId: string, status: ApprovalRequestView['status'] | 'all'): Promise<{ items: ApprovalRequestView[] }>;
  decideApproval(environmentId: string, requestId: string, decision: 'approve' | 'reject', comment?: string): Promise<ApprovalRequestView>;
  campaignDiff(environmentId: string, campaignId: string, from: number, to: number): Promise<RevisionDiff>;
  home(environmentId: string, query: { days?: number; app?: string }): Promise<HomeView>;
  recentObjects(environmentId: string): Promise<{ items: RecentObjectView[] }>;
  listPrivacyRequests(environmentId: string): Promise<{ items: PrivacyRequestView[]; nextCursor: string | null }>;
  listBrandThemes(environmentId: string): Promise<{ items: BrandThemeView[] }>;
  createBrandTheme(environmentId: string, body: { name: string; tokens: BrandTokens; isDefault?: boolean }): Promise<BrandThemeView>;
  updateBrandTheme(environmentId: string, themeId: string, body: { name?: string; tokens?: BrandTokens }): Promise<BrandThemeView>;
  makeDefaultBrandTheme(environmentId: string, themeId: string): Promise<BrandThemeView>;
  archiveBrandTheme(environmentId: string, themeId: string): Promise<void>;
  a11ySettings(environmentId: string): Promise<A11ySettings>;
  setA11ySettings(environmentId: string, body: A11ySettings): Promise<A11ySettings>;
  checkA11y(environmentId: string, body: { channel: 'content_card' | 'in_app' | 'banner'; content: Record<string, unknown>; themeId?: string }): Promise<A11yCheck>;
  mergeProfiles(environmentId: string, body: { source: { externalId: string }; target: { externalId: string }; reason: string; dryRun: boolean }): Promise<MergePreview>;
  createPrivacyRequest(environmentId: string, body: { requestId: string; kind: 'export' | 'delete'; user: { externalId: string }; reason?: string }): Promise<PrivacyRequestView>;
  findTombstones(environmentId: string, externalId: string): Promise<{ items: TombstoneView[] }>;
  releaseTombstone(environmentId: string, tombstoneId: string, reason: string): Promise<TombstoneView>;
  listEventExportConnectors(environmentId: string): Promise<{ items: EventExportConnectorView[] }>;
  createEventExportConnector(environmentId: string, body: CreateEventExportConnectorBody): Promise<EventExportConnectorView>;
  updateEventExportConnector(environmentId: string, connectorId: string, body: UpdateEventExportConnectorBody): Promise<EventExportConnectorView>;
  archiveEventExportConnector(environmentId: string, connectorId: string): Promise<void>;
  testEventExportConnector(environmentId: string, connectorId: string): Promise<EventExportTestResult>;
  replayEventExport(environmentId: string, connectorId: string, body: { from: string; to: string }): Promise<{ windows: number }>;
  eventExportWindows(environmentId: string, connectorId: string, cursor?: string): Promise<EventExportWindowsPage>;
  eventExportReconcile(environmentId: string, connectorId: string, date: string): Promise<EventExportReconcileView>;
  listImports(environmentId: string, cursor?: string): Promise<{ items: ImportSummary[]; nextCursor: string | null }>;
  uploadImport(environmentId: string, file: Blob, fileName: string): Promise<ImportView & { suggestedMapping: ImportColumnMapping[] }>;
  getImport(environmentId: string, importId: string): Promise<ImportView>;
  putImportMapping(environmentId: string, importId: string, body: { mapping: ImportColumnMapping[]; options: ImportOptions }): Promise<ImportView>;
  importAction(environmentId: string, importId: string, action: 'validate' | 'apply' | 'cancel'): Promise<ImportView>;
  channelDashboard(environmentId: string, channel: DashboardChannel, filters: DashboardFilters): Promise<ChannelDashboardView>;
  /** Steps of a Canvas with entered / waiting / advanced / exited / failed / sends and time in step (`reports.view`). */
  canvasAnalytics(environmentId: string, canvasId: string, query: CanvasAnalyticsQuery): Promise<CanvasAnalytics>;
  /** Waiting entries past what their step should hold them, oldest first (`reports.view`). */
  canvasStuckEntries(environmentId: string, canvasId: string, stepId?: string): Promise<{ entries: CanvasStuckEntry[] }>;
  /** Push counts, metrics and their definitions of an environment, a campaign or a Canvas (`reports.view`). */
  pushAnalytics(environmentId: string, filters: PushAnalyticsFilters): Promise<PushAnalyticsResponse>;
  /** Why pushes of the same scope were not sent (`suppressed` reasons) or failed (codes by stage). */
  pushSuppressedReasons(environmentId: string, filters: PushAnalyticsFilters): Promise<PushSuppressedResponse>;
  /** KPIs with formulas, series, a breakdown, Message Performance, control, conversions and API ids of a campaign (`reports.view`). */
  campaignAnalytics(environmentId: string, campaignId: string, query: CampaignAnalyticsQuery): Promise<CampaignAnalytics>;
  /** Variants against control with intervals, uplift, Holm-adjusted p-values and a verdict (`reports.view`). */
  campaignVariantStats(environmentId: string, campaignId: string, query: VariantStatsQuery): Promise<VariantStatsView>;
  canvasVariantStats(environmentId: string, canvasId: string, query: VariantStatsQuery): Promise<VariantStatsView>;
  /** A funnel from the first receipt (or Canvas entry) through up to six steps; a read sent as POST (`reports.view`). */
  funnelReport(environmentId: string, body: FunnelRequest): Promise<FunnelReport>;
  /** Day-N retention by start-day cohort; a read sent as POST (`reports.view`). */
  retentionReport(environmentId: string, body: RetentionRequest): Promise<RetentionReport>;
  /** Why messages were not delivered, newest first (`reports.view`); 400 `invalid_cursor`. */
  activityLog(environmentId: string, filters: ActivityLogFilters): Promise<ActivityLogPage>;
  alertRules(environmentId: string, objectType: AlertObjectType, objectId: string): Promise<{ items: AlertRuleView[] }>;
  createAlertRule(environmentId: string, body: AlertRuleInput): Promise<AlertRuleView>;
  updateAlertRule(environmentId: string, ruleId: string, body: Partial<Pick<AlertRuleInput, 'name' | 'threshold' | 'windowMinutes' | 'activeHours' | 'notify' | 'enabled'>>): Promise<AlertRuleView>;
  deleteAlertRule(environmentId: string, ruleId: string): Promise<void>;
  testAlertRule(environmentId: string, ruleId: string): Promise<{ sent: string[]; skipped: string[] }>;
  alertEvents(environmentId: string, objectId: string): Promise<{ items: AlertEventView[] }>;
  listCanvases(environmentId: string, filters: CanvasListFilters): Promise<CanvasList>;
  getCanvas(environmentId: string, canvasId: string): Promise<CanvasView>;
  canvasTemplates(environmentId: string): Promise<{ items: CanvasTemplateView[] }>;
  createCanvas(environmentId: string, body: { name: string; templateId?: string }): Promise<CanvasView>;
  canvasAction(environmentId: string, canvasId: string, action: 'duplicate' | 'archive' | 'restore'): Promise<CanvasView>;
  canvasDraft(environmentId: string, canvasId: string): Promise<CanvasDraftView>;
  saveCanvasDraft(environmentId: string, canvasId: string, body: { definition: CanvasDefinition; baseUpdatedAt: string }): Promise<CanvasDraftView>;
  validateCanvasDraft(environmentId: string, canvasId: string, definition: CanvasDefinition): Promise<CanvasValidation>;
  canvasStepTypes(environmentId: string): Promise<{ items: CanvasStepTypeView[] }>;
  previewCanvasDelay(environmentId: string, body: CanvasDelayPreviewBody): Promise<CanvasDelayPreview>;
  /** The step's variables worked out in order for one profile and an example event (`canvas.edit`). */
  previewCanvasContext(environmentId: string, body: CanvasContextPreviewBody): Promise<CanvasContextPreview>;
  canvasSettings(environmentId: string, canvasId: string): Promise<CanvasSettings>;
  saveCanvasSettings(environmentId: string, canvasId: string, settings: CanvasSettings): Promise<CanvasSettings>;
  publishCanvas(environmentId: string, canvasId: string, body: { migration?: CanvasMigration }): Promise<CanvasPublishResult>;
  pauseCanvas(environmentId: string, canvasId: string, mode: 'entries' | 'all'): Promise<CanvasView>;
  resumeCanvas(environmentId: string, canvasId: string): Promise<CanvasResumeResult>;
  stopCanvas(environmentId: string, canvasId: string, reason: string): Promise<CanvasView>;
  /** One profile through the saved draft or the running version, nothing stored or sent (`canvas.edit`); 422 `canvas_invalid` / `not_a_test_profile` / `test_send_unavailable`, 409 `not_published` / `simulation_conflict`, 429 `simulation_busy`. */
  simulateCanvas(environmentId: string, canvasId: string, body: CanvasSimulationBody): Promise<CanvasSimulationResult>;
  /** Domains webhook endpoints may call (`integrations.manage`). */
  webhookSettings(environmentId: string): Promise<WebhookSettings>;
  saveWebhookSettings(environmentId: string, settings: WebhookSettings): Promise<WebhookSettings>;
  /** Full views with `integrations.manage`, summaries for composers otherwise (`env.view`). */
  listWebhookEndpoints(environmentId: string): Promise<Array<WebhookEndpointView | WebhookEndpointSummary>>;
  /** 422 `webhook_domain_not_allowed` / `webhook_https_required` / `webhook_address_blocked` / `webhook_url_invalid` / `template_invalid` / `webhook_secret_invalid`, 409 `webhook_endpoint_name_taken`. */
  createWebhookEndpoint(environmentId: string, body: WebhookEndpointInput): Promise<WebhookEndpointView>;
  updateWebhookEndpoint(environmentId: string, endpointId: string, body: WebhookEndpointPatch): Promise<WebhookEndpointView>;
  /** Archives (the name is free again); messages that use it fail with `webhook_endpoint_missing`. */
  archiveWebhookEndpoint(environmentId: string, endpointId: string): Promise<void>;
  listCampaigns(environmentId: string, filters: CampaignListFilters): Promise<CampaignList>;
  /** 400 `validation_failed` when `to` is before `from` or the range is longer than 62 days. */
  calendar(environmentId: string, filters: CalendarFilters): Promise<CalendarView>;
  getCampaign(environmentId: string, campaignId: string): Promise<CampaignView>;
  createCampaign(environmentId: string, body: CreateCampaignInput): Promise<CampaignView>;
  campaignAction(environmentId: string, campaignId: string, action: Exclude<CampaignAction, 'archive'>, body?: { reason?: string }): Promise<CampaignView>;
  archiveCampaign(environmentId: string, campaignId: string): Promise<CampaignArchiveResult>;
  campaignRevisions(environmentId: string, campaignId: string): Promise<{ items: CampaignRevisionView[] }>;
  /** Saves the wizard's edits into the working draft (BB-97); 412 `revision_conflict` when someone saved in between. */
  updateCampaignDraft(environmentId: string, campaignId: string, body: UpdateCampaignDraftInput): Promise<CampaignView>;
  /** The wizard's aggregated check (BB-97): unsaved parts in `content` replace the stored ones; publish runs the same. */
  validateCampaignDraft(environmentId: string, campaignId: string, content?: Partial<CampaignContent>): Promise<CampaignDraftValidation>;
  /** Translators' CSV of the campaign's texts (BB-55): one row per text field, one column per locale. */
  translationsCsv(environmentId: string, campaignId: string): Promise<string>;
  /** Imports translators' CSV into the draft; 422 `translations_invalid` lists the rows and saves nothing. */
  importTranslations(environmentId: string, campaignId: string, draftVersion: number, csv: string): Promise<TranslationsImport>;
  schedulePreview(environmentId: string, body: { scheduled: ScheduledDelivery; count?: number }): Promise<SchedulePreview>;
  /** Variables for the personalization picker; a campaign adds its trigger's data (event or API trigger properties). */
  templatingVariables(environmentId: string, campaignId?: string): Promise<TemplateVariables>;
  /** The exact payload of one channel for one profile; 404 `profile_not_found` / `campaign_not_found`, 422 `no_content`. */
  previewPayload(environmentId: string, body: PayloadPreviewBody): Promise<PayloadPreview>;
  /** Sends the message to content-test profiles now (BB-59); 403 `test_recipient_not_allowed` lists `profileIds`, 422 `test_group_not_found` / `no_recipients`, 429 `rate_limited`. */
  testSend(environmentId: string, body: TestSendBody): Promise<TestSendResponse>;
  /** A 7-day link to the rendered message for colleagues; only this answer carries the `url` (the token is stored hashed). */
  createPreviewLink(environmentId: string, body: CreatePreviewLinkBody): Promise<CreatedPreviewLink>;
  /** The environment's latest 100 preview links (no tokens), optionally of one campaign. */
  listPreviewLinks(environmentId: string, campaignId?: string): Promise<PreviewLinkView[]>;
  revokePreviewLink(environmentId: string, linkId: string): Promise<PreviewLinkView>;
  /** `GET /public/v1/previews/:token` without a session; 404 `preview_link_unavailable` for unknown, expired or revoked links. */
  publicPreview(token: string): Promise<PublicPreview>;
  /** Checks a push content before publishing (BB-73, BB-76); 400 `validation_failed` lists structural `issues`. */
  validatePush(environmentId: string, body: PushValidateRequest): Promise<PushValidationResult>;
  /** The exact FCM / APNs payload of a push content for one profile, on every platform it is written for. */
  previewPush(environmentId: string, body: PushPreviewRequest): Promise<PushPreviewResult>;
  /** Push defaults of the environment (`environment_default` in send settings, BB-77). */
  pushSettings(environmentId: string): Promise<PushEnvironmentSettings>;
  pushAndroidChannels(environmentId: string): Promise<AndroidChannelView[]>;
  pushIosCategories(environmentId: string): Promise<IosCategoryView[]>;
  pushIosThreads(environmentId: string): Promise<IosThreadView[]>;
  appPushCapabilities(environmentId: string, appId: string): Promise<AppPushCapabilities>;
  /** Push settings page (BB-77): defaults and registry writes need `integrations.manage`, capabilities `apps.manage`. */
  putPushSettings(environmentId: string, body: PushEnvironmentSettings): Promise<PushEnvironmentSettings>;
  /** 409 `android_channel_conflict`: the channel id is taken or another channel is the fallback. */
  createPushAndroidChannel(environmentId: string, body: AndroidChannelBody): Promise<AndroidChannelView>;
  updatePushAndroidChannel(environmentId: string, id: string, body: UpdateAndroidChannelBody): Promise<AndroidChannelView>;
  archivePushAndroidChannel(environmentId: string, id: string): Promise<AndroidChannelView>;
  createPushIosCategory(environmentId: string, body: IosCategoryBody): Promise<IosCategoryView>;
  archivePushIosCategory(environmentId: string, id: string): Promise<IosCategoryView>;
  /** Creates or renames a thread (and brings an archived one back). */
  putPushIosThread(environmentId: string, body: IosThreadBody): Promise<IosThreadView>;
  putAppPushCapabilities(environmentId: string, appId: string, body: AppPushCapabilities): Promise<AppPushCapabilities>;
  /** The registry as Kotlin (Android app) or Swift (iOS app) to paste into the app. */
  pushSdkConfig(environmentId: string, appId: string): Promise<SdkConfigSnippet>;
  /** Push credential of an app (BB-72); 404 `push_credential_not_found` when there is none. All need `integrations.manage`. */
  pushCredential(environmentId: string, appId: string): Promise<PushCredentialView>;
  /** 422 `credential_invalid` (the reference does not resolve or parse) or `push_provider_mismatch`. */
  putPushCredential(environmentId: string, appId: string, body: PushCredentialBody): Promise<PushCredentialView>;
  /** Kill switch: off cancels queued pushes of the app. */
  setPushCredentialSending(environmentId: string, appId: string, sendingEnabled: boolean): Promise<PushCredentialView>;
  checkPushCredential(environmentId: string, appId: string): Promise<PushCredentialCheckView>;
  /** One push to a device token; also needs `messages.test_send`; 409 `push_sending_disabled` while the kill switch is off. */
  testSendPushCredential(environmentId: string, appId: string, body: PushTestSendBody): Promise<PushTestSendView>;
  /** P50/P95 of a transactional campaign over the last 24 hours against the 5 s SLA (BB-78). */
  transactionalLatency(environmentId: string, campaignId: string): Promise<TransactionalLatency>;
  getPreference<T>(key: string): Promise<{ key: string; value: T | null }>;
  putPreference<T>(key: string, value: T): Promise<{ key: string; value: T }>;
  exportColumns(environmentId: string): Promise<{ items: ExportColumnOption[] }>;
  requestExport(environmentId: string, target: { kind: 'segment'; segmentId: string } | { kind: 'global_control' }, columns: string[]): Promise<ExportView>;
  myExports(environmentId: string): Promise<{ items: ExportView[] }>;
  suppressionLists(environmentId: string): Promise<{ items: SuppressionListView[] }>;
  suppressionList(environmentId: string, id: string): Promise<SuppressionListView>;
  createSuppressionList(environmentId: string, body: { name: string; description?: string | null; definition: unknown; exceptionTagIds?: string[] }): Promise<SuppressionListView>;
  updateSuppressionList(environmentId: string, id: string, body: { name?: string; description?: string | null; definition?: unknown; exceptionTagIds?: string[] }): Promise<SuppressionListView>;
  setSuppressionListActive(environmentId: string, id: string, active: boolean): Promise<SuppressionListView>;
  archiveSuppressionList(environmentId: string, id: string): Promise<SuppressionListView>;
  globalControl(environmentId: string): Promise<GlobalControlView>;
  enableGlobalControl(environmentId: string, body: { percent: number; exemptTagIds?: string[] }): Promise<GlobalControlView>;
  disableGlobalControl(environmentId: string): Promise<GlobalControlView>;
  listFrequencyCaps(environmentId: string): Promise<{ items: FrequencyCapRuleView[] }>;
  createFrequencyCap(environmentId: string, body: FrequencyCapRuleBody): Promise<FrequencyCapRuleView>;
  updateFrequencyCap(environmentId: string, capId: string, body: UpdateFrequencyCapRuleBody): Promise<FrequencyCapRuleView>;
  deleteFrequencyCap(environmentId: string, capId: string): Promise<void>;
  rateLimits(environmentId: string): Promise<RateLimitsView>;
  putRateLimits(environmentId: string, body: PutRateLimitsBody): Promise<RateLimitsView>;
  eventLog(environmentId: string, filters: EventLogFilters): Promise<{ items: DebugLogSummary[]; lastId: string | null }>;
  eventLogEntry(environmentId: string, id: string): Promise<DebugLogEntry>;
  rejections(environmentId: string, days: number): Promise<{ items: RejectionRow[] }>;
  debugSessions(environmentId: string): Promise<{ items: DebugSessionView[] }>;
  createDebugSession(environmentId: string, body: { targetKind: 'installation' | 'external_id'; targetValue: string; minutes?: number; note?: string; collectSdkLogs?: boolean }): Promise<DebugSessionView>;
  sdkLogs(environmentId: string, sessionId: string, query: { level?: SdkLogLineView['level']; q?: string }): Promise<{ items: SdkLogLineView[]; truncated: boolean }>;
  endDebugSession(environmentId: string, id: string): Promise<DebugSessionView>;
  estimateAudience(environmentId: string, definition: unknown): Promise<AudienceReach>;
  lookupAudience(environmentId: string, definition: unknown, profile: { by: audience.LookupAudienceBody['profile']['by']; value: string }, locale: 'uk' | 'en'): Promise<AudienceLookup>;
  requestExactAudience(environmentId: string, definition: unknown): Promise<AudienceExactRequest>;
  getExactAudience(environmentId: string, requestId: string): Promise<AudienceExactRequest>;
  cancelExactAudience(environmentId: string, requestId: string): Promise<AudienceExactRequest>;
  listAliases(environmentId: string): Promise<NameAlias[]>;
  createAlias(environmentId: string, body: { kind: 'event' | 'attribute'; aliasName: string; canonicalName: string; note?: string; migrateExisting?: boolean }): Promise<unknown>;
  archiveAlias(environmentId: string, aliasId: string): Promise<unknown>;
  aliasUsage(environmentId: string, days: number): Promise<{ days: number; items: AliasUsageGroup[] }>;
  eventPicker(environmentId: string, query: string): Promise<EventPickerItem[]>;
  listAttributes(environmentId: string, filters: { query?: string; status?: string; piiClass?: string; reviewRequired?: boolean; tag?: string; cursor?: string }): Promise<{ items: AttributeSummary[]; nextCursor: string | null; reviewRequired: number }>;
  getAttribute(environmentId: string, key: string): Promise<AttributeDetails>;
  updateAttribute(environmentId: string, key: string, patch: AttributePatch): Promise<unknown>;
  listMedia(environmentId: string, filters: { query?: string; type?: string; tag?: string; archived?: boolean; cursor?: string }): Promise<{ items: MediaAssetSummary[]; nextCursor: string | null }>;
  getMedia(environmentId: string, assetId: string): Promise<MediaAssetDetails>;
  uploadMedia(environmentId: string, file: Blob, name: string, fileName?: string): Promise<MediaAssetDetails & { deduplicated: boolean }>;
  replaceMedia(environmentId: string, assetId: string, file: Blob, fileName?: string): Promise<MediaAssetDetails & { deduplicated: boolean }>;
  updateMedia(environmentId: string, assetId: string, patch: { name?: string; altText?: string | null }): Promise<MediaAssetDetails>;
  setMediaCrop(environmentId: string, assetId: string, versionId: string, preset: MediaPreset, crop: MediaCrop): Promise<MediaAssetDetails>;
  archiveMedia(environmentId: string, assetId: string): Promise<MediaAssetDetails>;
  restoreMedia(environmentId: string, assetId: string): Promise<MediaAssetDetails>;
  listTemplates(environmentId: string, filters: TemplateFilters): Promise<{ items: TemplateSummary[]; nextCursor: string | null }>;
  getTemplate(environmentId: string, templateId: string): Promise<TemplateDetails>;
  getTemplateVersion(environmentId: string, templateId: string, version: number): Promise<TemplateVersionView>;
  createTemplate(environmentId: string, body: { channel: TemplateChannel; name: string; description?: string; content: TemplateContent; notes?: string }): Promise<TemplateWriteResult>;
  updateTemplate(environmentId: string, templateId: string, patch: { name?: string; description?: string | null }): Promise<TemplateDetails>;
  addTemplateVersion(environmentId: string, templateId: string, body: { content: TemplateContent; notes?: string; baseVersion?: number }): Promise<TemplateWriteResult>;
  duplicateTemplate(environmentId: string, templateId: string): Promise<TemplateWriteResult>;
  archiveTemplate(environmentId: string, templateId: string): Promise<TemplateArchiveResult>;
  restoreTemplate(environmentId: string, templateId: string): Promise<TemplateDetails>;
  instantiateTemplate(environmentId: string, templateId: string, version?: number): Promise<TemplateInstance>;
  listContentBlocks(environmentId: string, filters: { query?: string; archived?: boolean }): Promise<{ items: ContentBlockSummary[]; nextCursor: string | null }>;
  getContentBlock(environmentId: string, blockId: string): Promise<ContentBlockDetails>;
  getContentBlockVersion(environmentId: string, blockId: string, version: number): Promise<ContentBlockVersionView>;
  /** 409 `content_block_key_taken` / `content_block_name_taken`, 422 `content_block_invalid` with `issues`. */
  createContentBlock(environmentId: string, body: { key: string; name: string; kind?: ContentBlockKind; description?: string; body: string; notes?: string }): Promise<ContentBlockDetails>;
  updateContentBlock(environmentId: string, blockId: string, patch: { name?: string; description?: string | null }): Promise<ContentBlockDetails>;
  addContentBlockVersion(environmentId: string, blockId: string, body: { body: string; notes?: string; baseVersion?: number }): Promise<ContentBlockDetails>;
  propagateContentBlock(environmentId: string, blockId: string, body: { toVersion: number; targets: Array<{ type: 'campaign' | 'canvas'; id: string }> }): Promise<ContentBlockPropagateResult>;
  archiveContentBlock(environmentId: string, blockId: string): Promise<ContentBlockArchiveResult>;
  restoreContentBlock(environmentId: string, blockId: string): Promise<ContentBlockDetails>;
}

export class ApiError extends Error {
  constructor(
    readonly status: number,
    readonly code: string,
    message: string,
    /** The whole problem+json body (e.g. `dependents` of `object_in_use`). */
    readonly problem: Record<string, unknown> = {},
  ) {
    super(message);
  }
}

const csrfToken = () => document.cookie.match(/(?:^|;\s*)bb_csrf=([^;]+)/)?.[1] ?? '';

async function request<T>(method: string, path: string, body?: unknown): Promise<T> {
  const headers: Record<string, string> = { accept: 'application/json' };
  if (method !== 'GET') headers['x-csrf-token'] = csrfToken();
  if (body !== undefined) headers['content-type'] = 'application/json';
  const res = await fetch(path, {
    method,
    credentials: 'include',
    headers,
    ...(body !== undefined ? { body: JSON.stringify(body) } : {}),
  });
  if (!res.ok) {
    const problem = (await res.json().catch(() => ({}))) as { code?: string; title?: string; detail?: string } & Record<string, unknown>;
    throw new ApiError(res.status, problem.code ?? 'http_error', problem.detail ?? problem.title ?? res.statusText, problem);
  }
  return (res.status === 204 ? undefined : await res.json()) as T;
}

const getJson = <T>(path: string) => request<T>('GET', path);

/** Query string of the set filters (empty and undefined values are left out). */
const searchOf = (filters: object) => new URLSearchParams(Object.entries(filters).flatMap(([key, value]) => (value === undefined || value === null || value === '' ? [] : [[key, String(value)]])));

/** A text download (CSV) with the same error handling as JSON calls. */
async function getText(path: string): Promise<string> {
  const res = await fetch(path, { method: 'GET', credentials: 'include', headers: { accept: 'text/csv, application/problem+json' } });
  if (!res.ok) {
    const problem = (await res.json().catch(() => ({}))) as { code?: string; title?: string; detail?: string } & Record<string, unknown>;
    throw new ApiError(res.status, problem.code ?? 'http_error', problem.detail ?? problem.title ?? res.statusText, problem);
  }
  return res.text();
}

/** File upload as the raw body (media library): the server reads the real type from the bytes. */
async function upload<T>(path: string, file: Blob): Promise<T> {
  const res = await fetch(path, {
    method: 'POST',
    credentials: 'include',
    headers: { accept: 'application/json', 'x-csrf-token': csrfToken(), 'content-type': file.type || 'application/octet-stream' },
    body: file,
  });
  if (!res.ok) {
    const problem = (await res.json().catch(() => ({}))) as { code?: string; title?: string; detail?: string } & Record<string, unknown>;
    throw new ApiError(res.status, problem.code ?? 'http_error', problem.detail ?? problem.title ?? res.statusText, problem);
  }
  return (await res.json()) as T;
}

export const httpAdminApi: AdminApi = {
  me: () => getJson('/admin/v1/me'),
  accessCatalog: () => getJson('/admin/v1/access/catalog'),
  listGrants: async (employeeId) => (await getJson<{ items: GrantSummary[] }>(`/admin/v1/employees/${employeeId}/grants`)).items,
  addGrant: (employeeId, grant) => request('POST', `/admin/v1/employees/${employeeId}/grants`, grant),
  removeGrant: (employeeId, grantId) => request('DELETE', `/admin/v1/employees/${employeeId}/grants/${grantId}`),
  listAudit: (filters) => {
    const query = new URLSearchParams(Object.entries(filters).filter((entry): entry is [string, string] => Boolean(entry[1])));
    return getJson(`/admin/v1/audit?${query}`);
  },
  objectChanges: (environmentId, objectType, objectId) =>
    getJson(`/admin/v1/environments/${environmentId}/objects/${objectType}/${encodeURIComponent(objectId)}/changes`),
  identifiers: (environmentId) => getJson(`/admin/v1/environments/${environmentId}/identifiers`),
  listInternalGroups: async (environmentId) =>
    (await getJson<{ items: InternalGroupSummary[] }>(`/admin/v1/environments/${environmentId}/internal-groups`)).items,
  createInternalGroup: (environmentId, group) => request('POST', `/admin/v1/environments/${environmentId}/internal-groups`, group),
  archiveInternalGroup: (environmentId, groupId) => request('POST', `/admin/v1/environments/${environmentId}/internal-groups/${groupId}/archive`),
  listGroupMembers: async (environmentId, groupId) =>
    (await getJson<{ items: GroupMember[] }>(`/admin/v1/environments/${environmentId}/internal-groups/${groupId}/members`)).items,
  addGroupMember: (environmentId, groupId, member) =>
    request('POST', `/admin/v1/environments/${environmentId}/internal-groups/${groupId}/members`, member),
  removeGroupMember: (environmentId, groupId, profileId) =>
    request('DELETE', `/admin/v1/environments/${environmentId}/internal-groups/${groupId}/members/${profileId}`),
  appDetails: (environmentId, appId) => getJson(`/admin/v1/environments/${environmentId}/apps/${appId}`),
  updateApp: (environmentId, appId, patch) => request('PATCH', `/admin/v1/environments/${environmentId}/apps/${appId}`, patch),
  createSdkKey: (environmentId, appId) => request('POST', `/admin/v1/environments/${environmentId}/apps/${appId}/sdk-keys`),
  rotateSdkKey: (environmentId, appId, keyId) =>
    request('POST', `/admin/v1/environments/${environmentId}/apps/${appId}/sdk-keys/${keyId}/rotate`, {}),
  revokeSdkKey: (environmentId, appId, keyId) =>
    request('POST', `/admin/v1/environments/${environmentId}/apps/${appId}/sdk-keys/${keyId}/revoke`),
  listServerKeys: async (environmentId) =>
    (await getJson<{ items: ServerKeySummary[] }>(`/admin/v1/environments/${environmentId}/server-keys`)).items,
  createServerKey: (environmentId, body) => request('POST', `/admin/v1/environments/${environmentId}/server-keys`, body),
  rotateServerKey: (environmentId, keyId, overlapHours) =>
    request('POST', `/admin/v1/environments/${environmentId}/server-keys/${keyId}/rotate`, { overlapHours }),
  revokeServerKey: (environmentId, keyId) => request('POST', `/admin/v1/environments/${environmentId}/server-keys/${keyId}/revoke`),
  markViewed: (environmentId, objectType, objectId) =>
    request('POST', `/admin/v1/environments/${environmentId}/objects/${objectType}/${encodeURIComponent(objectId)}/viewed`),
  listEmployees: async () => (await getJson<{ items: EmployeeSummary[] }>('/admin/v1/employees')).items,
  invite: (email) => request('POST', '/admin/v1/invites', { email }),
  deactivate: (employeeId) => request('POST', `/admin/v1/employees/${employeeId}/deactivate`),
  logout: () => request('POST', '/auth/logout'),
  listEnvironments: async () =>
    (await getJson<{ items: EnvironmentSummary[] }>('/admin/v1/me/environments')).items,
  updateEnvironment: (environmentId, patch) => request('PATCH', `/admin/v1/environments/${environmentId}`, patch),
  listApps: async (environmentId) =>
    (await getJson<{ items: AppInstanceSummary[] }>(`/admin/v1/environments/${environmentId}/apps`)).items,
  listTags: async (environmentId) => (await getJson<{ items: TagSummary[] }>(`/admin/v1/environments/${environmentId}/tags`)).items,
  createTag: (environmentId, body) => request('POST', `/admin/v1/environments/${environmentId}/tags`, body),
  updateTag: (environmentId, tagId, patch) => request('PATCH', `/admin/v1/environments/${environmentId}/tags/${tagId}`, patch),
  deleteTag: (environmentId, tagId, force) => request('DELETE', `/admin/v1/environments/${environmentId}/tags/${tagId}${force ? '?force=true' : ''}`),
  objectTags: async (environmentId, objectType, objectId) =>
    (await getJson<{ items: TagRef[] }>(`/admin/v1/environments/${environmentId}/objects/${objectType}/${objectId}/tags`)).items,
  setObjectTags: async (environmentId, objectType, objectId, tagIds) =>
    (await request<{ items: TagRef[] }>('PUT', `/admin/v1/environments/${environmentId}/objects/${objectType}/${objectId}/tags`, { tagIds })).items,
  listRetention: async (environmentId) => (await getJson<{ items: RetentionPolicy[] }>(`/admin/v1/environments/${environmentId}/retention`)).items,
  updateRetention: async (environmentId, policies) =>
    (await request<{ items: RetentionPolicy[] }>('PUT', `/admin/v1/environments/${environmentId}/retention`, { policies })).items,
  listSubscriptionGroups: async (environmentId, filters) => {
    const query = new URLSearchParams(Object.entries(filters).filter((entry): entry is [string, string] => Boolean(entry[1])));
    return (await getJson<{ items: SubscriptionGroupSummary[] }>(`/admin/v1/environments/${environmentId}/subscription-groups?${query}`)).items;
  },
  getSubscriptionGroup: (environmentId, groupId) => getJson(`/admin/v1/environments/${environmentId}/subscription-groups/${groupId}`),
  createSubscriptionGroup: (environmentId, body) => request('POST', `/admin/v1/environments/${environmentId}/subscription-groups`, body),
  archiveSubscriptionGroup: (environmentId, groupId) => request('POST', `/admin/v1/environments/${environmentId}/subscription-groups/${groupId}/archive`),
  subscriptionGroupStats: async (environmentId, groupId, days) =>
    (await getJson<{ items: Array<{ day: string; subscribed: number; unsubscribed: number }> }>(`/admin/v1/environments/${environmentId}/subscription-groups/${groupId}/stats?days=${days}`)).items,
  calculateSubscriptionGroup: (environmentId, groupId) => request('POST', `/admin/v1/environments/${environmentId}/subscription-groups/${groupId}/stats/calculate`),
  listEvents: (environmentId, filters) => {
    const query = new URLSearchParams(Object.entries(filters).filter((entry): entry is [string, string] => Boolean(entry[1])));
    return getJson(`/admin/v1/environments/${environmentId}/catalog/events?${query}`);
  },
  getEvent: (environmentId, name) => getJson(`/admin/v1/environments/${environmentId}/catalog/events/${encodeURIComponent(name)}`),
  createEvent: (environmentId, body) => request('POST', `/admin/v1/environments/${environmentId}/catalog/events`, body),
  updateEvent: (environmentId, name, patch) => request('PATCH', `/admin/v1/environments/${environmentId}/catalog/events/${encodeURIComponent(name)}`, patch),
  listAttributes: (environmentId, filters) => {
    const query = new URLSearchParams(
      Object.entries({ ...filters, reviewRequired: filters.reviewRequired ? 'true' : undefined }).filter((entry): entry is [string, string] => typeof entry[1] === 'string' && entry[1] !== ''),
    );
    return getJson(`/admin/v1/environments/${environmentId}/catalog/attributes?${query}`);
  },
  listAliases: async (environmentId) => (await getJson<{ items: NameAlias[] }>(`/admin/v1/environments/${environmentId}/catalog/aliases`)).items,
  createAlias: (environmentId, body) => request('POST', `/admin/v1/environments/${environmentId}/catalog/aliases`, body),
  archiveAlias: (environmentId, aliasId) => request('DELETE', `/admin/v1/environments/${environmentId}/catalog/aliases/${aliasId}`),
  aliasUsage: (environmentId, days) => getJson(`/admin/v1/environments/${environmentId}/catalog/aliases/usage?days=${days}`),
  filterCatalog: (environmentId) => getJson(`/admin/v1/environments/${environmentId}/audience/filter-catalog`),
  describeAudience: (environmentId, definition, locale) => request('POST', `/admin/v1/environments/${environmentId}/audience/describe`, { definition, locale }),
  listSegments: (environmentId, filters) => {
    const params = new URLSearchParams();
    if (filters.query) params.set('query', filters.query);
    if (filters.tagId) params.set('tagId', filters.tagId);
    if (filters.starred) params.set('starred', 'true');
    if (filters.status) params.set('status', filters.status);
    if (filters.cursor) params.set('cursor', filters.cursor);
    return getJson(`/admin/v1/environments/${environmentId}/segments?${params}`);
  },
  getSegment: (environmentId, segmentId) => getJson(`/admin/v1/environments/${environmentId}/segments/${segmentId}`),
  extrasSchema: (environmentId, channel) => getJson(`/admin/v1/environments/${environmentId}/extras-schema?channel=${channel}`),
  createExtra: (environmentId, body) => request('POST', `/admin/v1/environments/${environmentId}/extras-schema`, body),
  updateExtra: (environmentId, id, body) => request('PATCH', `/admin/v1/environments/${environmentId}/extras-schema/${id}`, body),
  deleteExtra: (environmentId, id) => request('DELETE', `/admin/v1/environments/${environmentId}/extras-schema/${id}`),
  setExtrasStrict: (environmentId, channel, strict) => request('PUT', `/admin/v1/environments/${environmentId}/extras-schema/strict`, { channel, strict }),
  validateExtras: (environmentId, channel, extras) => request('POST', `/admin/v1/environments/${environmentId}/extras-schema/validate`, { channel, extras }),
  listDeeplinks: (environmentId, filters) => {
    const params = new URLSearchParams();
    if (filters.query) params.set('query', filters.query);
    if (filters.platform) params.set('platform', filters.platform);
    if (filters.includeDeprecated) params.set('includeDeprecated', 'true');
    return getJson(`/admin/v1/environments/${environmentId}/deeplinks?${params}`);
  },
  validateDeeplink: (environmentId, uri, platform) => getJson(`/admin/v1/environments/${environmentId}/deeplinks/validate?uri=${encodeURIComponent(uri)}${platform ? `&platform=${platform}` : ''}`),
  buildDeeplink: (environmentId, routeId, params) => request('POST', `/admin/v1/environments/${environmentId}/deeplinks/build`, { routeId, params }),
  createDeeplink: (environmentId, body) => request('POST', `/admin/v1/environments/${environmentId}/deeplinks`, body),
  updateDeeplink: (environmentId, id, body) => request('PATCH', `/admin/v1/environments/${environmentId}/deeplinks/${id}`, body),
  deleteDeeplink: (environmentId, id) => request('DELETE', `/admin/v1/environments/${environmentId}/deeplinks/${id}`),
  commentThreads: (environmentId, objectType, objectId, status) => getJson(`/admin/v1/environments/${environmentId}/objects/${objectType}/${objectId}/comment-threads?status=${status}`),
  createThread: (environmentId, objectType, objectId, body) => request('POST', `/admin/v1/environments/${environmentId}/objects/${objectType}/${objectId}/comment-threads`, body),
  replyThread: (environmentId, threadId, body) => request('POST', `/admin/v1/environments/${environmentId}/comment-threads/${threadId}/comments`, body),
  setThreadStatus: (environmentId, threadId, action) => request('POST', `/admin/v1/environments/${environmentId}/comment-threads/${threadId}/${action}`),
  deleteComment: (environmentId, commentId) => request('DELETE', `/admin/v1/environments/${environmentId}/comments/${commentId}`),
  mentionCandidates: (environmentId, query) => getJson(`/admin/v1/environments/${environmentId}/comment-mentions?query=${encodeURIComponent(query)}`),
  approvalPolicy: (environmentId) => getJson(`/admin/v1/environments/${environmentId}/approval-policy`),
  setApprovalPolicy: (environmentId, policy) => request('PUT', `/admin/v1/environments/${environmentId}/approval-policy`, policy),
  requestApproval: (environmentId, campaignId, comment) => request('POST', `/admin/v1/environments/${environmentId}/campaigns/${campaignId}/approval-requests`, comment ? { comment } : {}),
  approvalRequests: (environmentId, status) => getJson(`/admin/v1/environments/${environmentId}/approval-requests?status=${status}`),
  decideApproval: (environmentId, requestId, decision, comment) => request('POST', `/admin/v1/environments/${environmentId}/approval-requests/${requestId}/${decision}`, comment ? { comment } : {}),
  campaignDiff: (environmentId, campaignId, from, to) => getJson(`/admin/v1/environments/${environmentId}/campaigns/${campaignId}/revisions/${from}/diff/${to}`),
  home: (environmentId, query) => {
    const params = new URLSearchParams();
    if (query.days) params.set('days', String(query.days));
    if (query.app) params.set('app', query.app);
    return getJson(`/admin/v1/environments/${environmentId}/home?${params}`);
  },
  recentObjects: (environmentId) => getJson(`/admin/v1/environments/${environmentId}/home/recent-objects`),
  mergeProfiles: (environmentId, body) => request('POST', `/admin/v1/environments/${environmentId}/profiles/merge`, body),
  listBrandThemes: (environmentId) => getJson(`/admin/v1/environments/${environmentId}/brand-themes`),
  createBrandTheme: (environmentId, body) => request('POST', `/admin/v1/environments/${environmentId}/brand-themes`, body),
  updateBrandTheme: (environmentId, themeId, body) => request('PATCH', `/admin/v1/environments/${environmentId}/brand-themes/${themeId}`, body),
  makeDefaultBrandTheme: (environmentId, themeId) => request('POST', `/admin/v1/environments/${environmentId}/brand-themes/${themeId}/default`),
  archiveBrandTheme: (environmentId, themeId) => request('DELETE', `/admin/v1/environments/${environmentId}/brand-themes/${themeId}`),
  a11ySettings: (environmentId) => getJson(`/admin/v1/environments/${environmentId}/a11y-settings`),
  setA11ySettings: (environmentId, body) => request('PUT', `/admin/v1/environments/${environmentId}/a11y-settings`, body),
  checkA11y: (environmentId, body) => request('POST', `/admin/v1/environments/${environmentId}/a11y/validate`, body),
  listPrivacyRequests: (environmentId) => getJson(`/admin/v1/environments/${environmentId}/privacy/requests`),
  createPrivacyRequest: (environmentId, body) => request('POST', `/admin/v1/environments/${environmentId}/privacy/requests`, body),
  findTombstones: (environmentId, externalId) => getJson(`/admin/v1/environments/${environmentId}/privacy/tombstones?externalId=${encodeURIComponent(externalId)}`),
  releaseTombstone: (environmentId, tombstoneId, reason) => request('POST', `/admin/v1/environments/${environmentId}/privacy/tombstones/${tombstoneId}/release`, { reason }),
  listEventExportConnectors: (environmentId) => getJson(`/admin/v1/environments/${environmentId}/event-export/connectors`),
  createEventExportConnector: (environmentId, body) => request('POST', `/admin/v1/environments/${environmentId}/event-export/connectors`, body),
  updateEventExportConnector: (environmentId, connectorId, body) => request('PATCH', `/admin/v1/environments/${environmentId}/event-export/connectors/${connectorId}`, body),
  archiveEventExportConnector: (environmentId, connectorId) => request('DELETE', `/admin/v1/environments/${environmentId}/event-export/connectors/${connectorId}`),
  testEventExportConnector: (environmentId, connectorId) => request('POST', `/admin/v1/environments/${environmentId}/event-export/connectors/${connectorId}/test`),
  replayEventExport: (environmentId, connectorId, body) => request('POST', `/admin/v1/environments/${environmentId}/event-export/connectors/${connectorId}/replay`, body),
  eventExportWindows: (environmentId, connectorId, cursor) =>
    getJson(`/admin/v1/environments/${environmentId}/event-export/connectors/${connectorId}/windows${cursor ? `?${new URLSearchParams({ cursor })}` : ''}`),
  eventExportReconcile: (environmentId, connectorId, date) => getJson(`/admin/v1/environments/${environmentId}/event-export/connectors/${connectorId}/reconcile?${new URLSearchParams({ date })}`),
  listImports: (environmentId, cursor) => getJson(`/admin/v1/environments/${environmentId}/imports${cursor ? `?cursor=${cursor}` : ''}`),
  uploadImport: (environmentId, file, fileName) => upload(`/admin/v1/environments/${environmentId}/imports?${new URLSearchParams({ fileName })}`, file),
  getImport: (environmentId, importId) => getJson(`/admin/v1/environments/${environmentId}/imports/${importId}`),
  putImportMapping: (environmentId, importId, body) => request('PUT', `/admin/v1/environments/${environmentId}/imports/${importId}/mapping`, body),
  importAction: (environmentId, importId, action) => request('POST', `/admin/v1/environments/${environmentId}/imports/${importId}/${action}`),
  channelDashboard: (environmentId, channel, filters) => {
    const params = new URLSearchParams();
    for (const [key, value] of Object.entries(filters)) if (value !== undefined && value !== '') params.set(key, String(value));
    return getJson(`/admin/v1/environments/${environmentId}/dashboards/${channel}?${params}`);
  },
  canvasAnalytics: (environmentId, canvasId, query) => getJson(`/admin/v1/environments/${environmentId}/canvases/${canvasId}/analytics?${searchOf(query)}`),
  canvasStuckEntries: (environmentId, canvasId, stepId) => getJson(`/admin/v1/environments/${environmentId}/canvases/${canvasId}/stuck${stepId ? `?${new URLSearchParams({ stepId })}` : ''}`),
  pushAnalytics: (environmentId, filters) => getJson(`/admin/v1/environments/${environmentId}/analytics/push?${searchOf(filters)}`),
  pushSuppressedReasons: (environmentId, filters) => getJson(`/admin/v1/environments/${environmentId}/analytics/push/suppressed?${searchOf(filters)}`),
  campaignAnalytics: (environmentId, campaignId, query) => getJson(`/admin/v1/environments/${environmentId}/campaigns/${campaignId}/analytics?${searchOf(query)}`),
  campaignVariantStats: (environmentId, campaignId, query) => getJson(`/admin/v1/environments/${environmentId}/campaigns/${campaignId}/variants-stats?${searchOf(query)}`),
  canvasVariantStats: (environmentId, canvasId, query) => getJson(`/admin/v1/environments/${environmentId}/canvases/${canvasId}/variants-stats?${searchOf(query)}`),
  funnelReport: (environmentId, body) => request('POST', `/admin/v1/environments/${environmentId}/reports/funnel`, body),
  retentionReport: (environmentId, body) => request('POST', `/admin/v1/environments/${environmentId}/reports/retention`, body),
  activityLog: (environmentId, filters) => getJson(`/admin/v1/environments/${environmentId}/activity-log?${searchOf(filters)}`),
  alertRules: (environmentId, objectType, objectId) => getJson(`/admin/v1/environments/${environmentId}/alert-rules?objectType=${objectType}&objectId=${objectId}`),
  createAlertRule: (environmentId, body) => request('POST', `/admin/v1/environments/${environmentId}/alert-rules`, body),
  updateAlertRule: (environmentId, ruleId, body) => request('PATCH', `/admin/v1/environments/${environmentId}/alert-rules/${ruleId}`, body),
  deleteAlertRule: (environmentId, ruleId) => request('DELETE', `/admin/v1/environments/${environmentId}/alert-rules/${ruleId}`),
  testAlertRule: (environmentId, ruleId) => request('POST', `/admin/v1/environments/${environmentId}/alert-rules/${ruleId}/test`),
  alertEvents: (environmentId, objectId) => getJson(`/admin/v1/environments/${environmentId}/alert-events?objectId=${objectId}`),
  listCanvases: (environmentId, filters) => {
    const params = new URLSearchParams();
    for (const [key, value] of Object.entries(filters)) if (value) params.set(key, String(value));
    return getJson(`/admin/v1/environments/${environmentId}/canvases?${params}`);
  },
  getCanvas: (environmentId, canvasId) => getJson(`/admin/v1/environments/${environmentId}/canvases/${canvasId}`),
  canvasTemplates: (environmentId) => getJson(`/admin/v1/environments/${environmentId}/canvas-templates`),
  createCanvas: (environmentId, body) => request('POST', `/admin/v1/environments/${environmentId}/canvases`, body),
  canvasAction: (environmentId, canvasId, action) => request('POST', `/admin/v1/environments/${environmentId}/canvases/${canvasId}/${action}`),
  canvasDraft: (environmentId, canvasId) => getJson(`/admin/v1/environments/${environmentId}/canvases/${canvasId}/draft`),
  saveCanvasDraft: (environmentId, canvasId, body) => request('PUT', `/admin/v1/environments/${environmentId}/canvases/${canvasId}/draft`, body),
  validateCanvasDraft: (environmentId, canvasId, definition) => request('POST', `/admin/v1/environments/${environmentId}/canvases/${canvasId}/draft/validate`, { definition }),
  canvasStepTypes: (environmentId) => getJson(`/admin/v1/environments/${environmentId}/canvas-step-types`),
  previewCanvasDelay: (environmentId, body) => request('POST', `/admin/v1/environments/${environmentId}/canvas-steps/delay/preview`, body),
  previewCanvasContext: (environmentId, body) => request('POST', `/admin/v1/environments/${environmentId}/canvas-steps/context/preview`, body),
  canvasSettings: (environmentId, canvasId) => getJson(`/admin/v1/environments/${environmentId}/canvases/${canvasId}/settings`),
  saveCanvasSettings: (environmentId, canvasId, settings) => request('PUT', `/admin/v1/environments/${environmentId}/canvases/${canvasId}/settings`, settings),
  publishCanvas: (environmentId, canvasId, body) => request('POST', `/admin/v1/environments/${environmentId}/canvases/${canvasId}/publish`, body),
  pauseCanvas: (environmentId, canvasId, mode) => request('POST', `/admin/v1/environments/${environmentId}/canvases/${canvasId}/pause`, { mode }),
  resumeCanvas: (environmentId, canvasId) => request('POST', `/admin/v1/environments/${environmentId}/canvases/${canvasId}/resume`),
  stopCanvas: (environmentId, canvasId, reason) => request('POST', `/admin/v1/environments/${environmentId}/canvases/${canvasId}/stop`, { reason }),
  simulateCanvas: (environmentId, canvasId, body) => request('POST', `/admin/v1/environments/${environmentId}/canvases/${canvasId}/simulations`, body),
  webhookSettings: (environmentId) => getJson(`/admin/v1/environments/${environmentId}/webhook-settings`),
  saveWebhookSettings: (environmentId, settings) => request('PUT', `/admin/v1/environments/${environmentId}/webhook-settings`, settings),
  listWebhookEndpoints: async (environmentId) => (await getJson<{ items: Array<WebhookEndpointView | WebhookEndpointSummary> }>(`/admin/v1/environments/${environmentId}/webhook-endpoints`)).items,
  createWebhookEndpoint: (environmentId, body) => request('POST', `/admin/v1/environments/${environmentId}/webhook-endpoints`, body),
  updateWebhookEndpoint: (environmentId, endpointId, body) => request('PATCH', `/admin/v1/environments/${environmentId}/webhook-endpoints/${endpointId}`, body),
  archiveWebhookEndpoint: (environmentId, endpointId) => request('DELETE', `/admin/v1/environments/${environmentId}/webhook-endpoints/${endpointId}`),
  listCampaigns: (environmentId, filters) => {
    const params = new URLSearchParams();
    for (const [key, value] of Object.entries(filters)) if (value) params.set(key, String(value));
    return getJson(`/admin/v1/environments/${environmentId}/campaigns?${params}`);
  },
  calendar: (environmentId, filters) => {
    const params = new URLSearchParams();
    for (const [key, value] of Object.entries(filters)) if (value) params.set(key, String(value));
    return getJson(`/admin/v1/environments/${environmentId}/calendar?${params}`);
  },
  getCampaign: (environmentId, campaignId) => getJson(`/admin/v1/environments/${environmentId}/campaigns/${campaignId}`),
  createCampaign: (environmentId, body) => request('POST', `/admin/v1/environments/${environmentId}/campaigns`, body),
  campaignAction: (environmentId, campaignId, action, body) => request('POST', `/admin/v1/environments/${environmentId}/campaigns/${campaignId}/${action}`, body),
  archiveCampaign: (environmentId, campaignId) => request('POST', `/admin/v1/environments/${environmentId}/campaigns/${campaignId}/archive`),
  campaignRevisions: (environmentId, campaignId) => getJson(`/admin/v1/environments/${environmentId}/campaigns/${campaignId}/revisions`),
  updateCampaignDraft: (environmentId, campaignId, body) => request('PATCH', `/admin/v1/environments/${environmentId}/campaigns/${campaignId}/draft`, body),
  validateCampaignDraft: (environmentId, campaignId, content) => request('POST', `/admin/v1/environments/${environmentId}/campaigns/${campaignId}/draft/validate`, content ? { content } : {}),
  translationsCsv: (environmentId, campaignId) => getText(`/admin/v1/environments/${environmentId}/campaigns/${campaignId}/translations.csv`),
  importTranslations: (environmentId, campaignId, draftVersion, csv) =>
    upload(`/admin/v1/environments/${environmentId}/campaigns/${campaignId}/translations.csv?draftVersion=${draftVersion}`, new Blob([csv], { type: 'text/csv' })),
  schedulePreview: (environmentId, body) => request('POST', `/admin/v1/environments/${environmentId}/campaigns/schedule-preview`, body),
  templatingVariables: (environmentId, campaignId) =>
    getJson(`/admin/v1/environments/${environmentId}/templating/variables${campaignId ? `?${new URLSearchParams({ campaignId })}` : ''}`),
  previewPayload: (environmentId, body) => request('POST', `/admin/v1/environments/${environmentId}/templating/preview`, body),
  testSend: (environmentId, body) => request('POST', `/admin/v1/environments/${environmentId}/messages/test-send`, body),
  createPreviewLink: (environmentId, body) => request('POST', `/admin/v1/environments/${environmentId}/messages/preview-links`, body),
  listPreviewLinks: async (environmentId, campaignId) =>
    (await getJson<{ items: PreviewLinkView[] }>(`/admin/v1/environments/${environmentId}/messages/preview-links?${searchOf({ campaignId })}`)).items,
  revokePreviewLink: (environmentId, linkId) => request('POST', `/admin/v1/environments/${environmentId}/messages/preview-links/${linkId}/revoke`),
  publicPreview: (token) => getJson(`/public/v1/previews/${encodeURIComponent(token)}`),
  validatePush: (environmentId, body) => request('POST', `/admin/v1/environments/${environmentId}/push/validate`, body),
  previewPush: (environmentId, body) => request('POST', `/admin/v1/environments/${environmentId}/push/preview`, body),
  pushSettings: (environmentId) => getJson(`/admin/v1/environments/${environmentId}/push/settings`),
  pushAndroidChannels: async (environmentId) => (await getJson<{ items: AndroidChannelView[] }>(`/admin/v1/environments/${environmentId}/push/android-channels`)).items,
  pushIosCategories: async (environmentId) => (await getJson<{ items: IosCategoryView[] }>(`/admin/v1/environments/${environmentId}/push/ios-categories`)).items,
  pushIosThreads: async (environmentId) => (await getJson<{ items: IosThreadView[] }>(`/admin/v1/environments/${environmentId}/push/ios-threads`)).items,
  appPushCapabilities: (environmentId, appId) => getJson(`/admin/v1/environments/${environmentId}/apps/${appId}/push-capabilities`),
  putPushSettings: (environmentId, body) => request('PUT', `/admin/v1/environments/${environmentId}/push/settings`, body),
  createPushAndroidChannel: (environmentId, body) => request('POST', `/admin/v1/environments/${environmentId}/push/android-channels`, body),
  updatePushAndroidChannel: (environmentId, id, body) => request('PATCH', `/admin/v1/environments/${environmentId}/push/android-channels/${id}`, body),
  archivePushAndroidChannel: (environmentId, id) => request('POST', `/admin/v1/environments/${environmentId}/push/android-channels/${id}/archive`),
  createPushIosCategory: (environmentId, body) => request('POST', `/admin/v1/environments/${environmentId}/push/ios-categories`, body),
  archivePushIosCategory: (environmentId, id) => request('POST', `/admin/v1/environments/${environmentId}/push/ios-categories/${id}/archive`),
  putPushIosThread: (environmentId, body) => request('PUT', `/admin/v1/environments/${environmentId}/push/ios-threads`, body),
  putAppPushCapabilities: (environmentId, appId, body) => request('PUT', `/admin/v1/environments/${environmentId}/apps/${appId}/push-capabilities`, body),
  pushSdkConfig: (environmentId, appId) => getJson(`/admin/v1/environments/${environmentId}/push/sdk-config?${new URLSearchParams({ appId })}`),
  pushCredential: (environmentId, appId) => getJson(`/admin/v1/environments/${environmentId}/apps/${appId}/push-credential`),
  putPushCredential: (environmentId, appId, body) => request('PUT', `/admin/v1/environments/${environmentId}/apps/${appId}/push-credential`, body),
  setPushCredentialSending: (environmentId, appId, sendingEnabled) => request('PATCH', `/admin/v1/environments/${environmentId}/apps/${appId}/push-credential`, { sendingEnabled }),
  checkPushCredential: (environmentId, appId) => request('POST', `/admin/v1/environments/${environmentId}/apps/${appId}/push-credential/check`),
  testSendPushCredential: (environmentId, appId, body) => request('POST', `/admin/v1/environments/${environmentId}/apps/${appId}/push-credential/test-send`, body),
  transactionalLatency: (environmentId, campaignId) => getJson(`/admin/v1/environments/${environmentId}/campaigns/${campaignId}/transactional/latency`),
  getPreference: (key) => getJson(`/admin/v1/me/preferences/${encodeURIComponent(key)}`),
  putPreference: (key, value) => request('PUT', `/admin/v1/me/preferences/${encodeURIComponent(key)}`, { value }),
  createSegment: (environmentId, body) => request('POST', `/admin/v1/environments/${environmentId}/segments`, body),
  updateSegment: (environmentId, segmentId, body) => request('PATCH', `/admin/v1/environments/${environmentId}/segments/${segmentId}`, body),
  archiveSegment: (environmentId, segmentId) => request('POST', `/admin/v1/environments/${environmentId}/segments/${segmentId}/archive`),
  whereUsed: (environmentId, objectType, objectId) => getJson(`/admin/v1/environments/${environmentId}/objects/${objectType}/${encodeURIComponent(objectId)}/where-used`),
  restoreSegment: (environmentId, segmentId) => request('POST', `/admin/v1/environments/${environmentId}/segments/${segmentId}/restore`),
  duplicateSegment: (environmentId, segmentId) => request('POST', `/admin/v1/environments/${environmentId}/segments/${segmentId}/duplicate`),
  starSegment: (environmentId, segmentId, starred) => request('POST', `/admin/v1/environments/${environmentId}/segments/${segmentId}/${starred ? 'star' : 'unstar'}`),
  segmentRevisions: (environmentId, segmentId, locale) => getJson(`/admin/v1/environments/${environmentId}/segments/${segmentId}/revisions?locale=${locale}`),
  segmentHistory: (environmentId, segmentId, days) => getJson(`/admin/v1/environments/${environmentId}/segments/${segmentId}/history?days=${days}`),
  segmentInsights: (environmentId, segmentIds) => request('POST', `/admin/v1/environments/${environmentId}/segments/insights`, { segmentIds }),
  segmentCampaignDraft: (environmentId, segmentId) => request('POST', `/admin/v1/environments/${environmentId}/segments/${segmentId}/create-campaign`),
  exportColumns: (environmentId) => getJson(`/admin/v1/environments/${environmentId}/exports/columns`),
  requestExport: (environmentId, target, columns) =>
    request('POST', target.kind === 'segment' ? `/admin/v1/environments/${environmentId}/segments/${target.segmentId}/exports` : `/admin/v1/environments/${environmentId}/global-control/exports`, { columns }),
  myExports: (environmentId) => getJson(`/admin/v1/environments/${environmentId}/exports`),
  suppressionLists: (environmentId) => getJson(`/admin/v1/environments/${environmentId}/suppression-lists`),
  suppressionList: (environmentId, id) => getJson(`/admin/v1/environments/${environmentId}/suppression-lists/${id}`),
  createSuppressionList: (environmentId, body) => request('POST', `/admin/v1/environments/${environmentId}/suppression-lists`, body),
  updateSuppressionList: (environmentId, id, body) => request('PATCH', `/admin/v1/environments/${environmentId}/suppression-lists/${id}`, body),
  setSuppressionListActive: (environmentId, id, active) => request('POST', `/admin/v1/environments/${environmentId}/suppression-lists/${id}/${active ? 'activate' : 'deactivate'}`),
  archiveSuppressionList: (environmentId, id) => request('POST', `/admin/v1/environments/${environmentId}/suppression-lists/${id}/archive`),
  globalControl: (environmentId) => getJson(`/admin/v1/environments/${environmentId}/global-control`),
  enableGlobalControl: (environmentId, body) => request('POST', `/admin/v1/environments/${environmentId}/global-control/enable`, body),
  disableGlobalControl: (environmentId) => request('POST', `/admin/v1/environments/${environmentId}/global-control/disable`),
  listFrequencyCaps: (environmentId) => getJson(`/admin/v1/environments/${environmentId}/frequency-caps`),
  createFrequencyCap: (environmentId, body) => request('POST', `/admin/v1/environments/${environmentId}/frequency-caps`, body),
  updateFrequencyCap: (environmentId, capId, body) => request('PATCH', `/admin/v1/environments/${environmentId}/frequency-caps/${capId}`, body),
  deleteFrequencyCap: (environmentId, capId) => request('DELETE', `/admin/v1/environments/${environmentId}/frequency-caps/${capId}`),
  rateLimits: (environmentId) => getJson(`/admin/v1/environments/${environmentId}/rate-limits`),
  putRateLimits: (environmentId, body) => request('PUT', `/admin/v1/environments/${environmentId}/rate-limits`, body),
  eventLog: (environmentId, filters) => {
    const params = new URLSearchParams(Object.entries(filters).filter((e): e is [string, string] => typeof e[1] === 'string' && e[1] !== ''));
    return getJson(`/admin/v1/environments/${environmentId}/debug/event-log?${params}`);
  },
  eventLogEntry: (environmentId, id) => getJson(`/admin/v1/environments/${environmentId}/debug/event-log/${id}`),
  rejections: (environmentId, days) => getJson(`/admin/v1/environments/${environmentId}/debug/rejections?days=${days}`),
  debugSessions: (environmentId) => getJson(`/admin/v1/environments/${environmentId}/debug/sessions`),
  createDebugSession: (environmentId, body) => request('POST', `/admin/v1/environments/${environmentId}/debug/sessions`, body),
  sdkLogs: (environmentId, sessionId, query) => {
    const params = new URLSearchParams();
    if (query.level) params.set('level', query.level);
    if (query.q) params.set('q', query.q);
    return getJson(`/admin/v1/environments/${environmentId}/debug/sessions/${sessionId}/sdk-logs?${params}`);
  },
  endDebugSession: (environmentId, id) => request('POST', `/admin/v1/environments/${environmentId}/debug/sessions/${id}/end`),
  estimateAudience: (environmentId, definition) => request('POST', `/admin/v1/environments/${environmentId}/audience/estimate`, { definition }),
  lookupAudience: (environmentId, definition, profile, locale) => request('POST', `/admin/v1/environments/${environmentId}/audience/lookup`, { definition, profile, locale }),
  requestExactAudience: (environmentId, definition) => request('POST', `/admin/v1/environments/${environmentId}/audience/exact`, { definition }),
  getExactAudience: (environmentId, requestId) => getJson(`/admin/v1/environments/${environmentId}/audience/exact/${requestId}`),
  cancelExactAudience: (environmentId, requestId) => request('DELETE', `/admin/v1/environments/${environmentId}/audience/exact/${requestId}`),
  updateEventProperty: (environmentId, name, path, patch) =>
    request('PATCH', `/admin/v1/environments/${environmentId}/catalog/events/${encodeURIComponent(name)}/properties/${encodeURIComponent(path)}`, patch),
  trustReport: (environmentId, days) => getJson(`/admin/v1/environments/${environmentId}/catalog/trust?days=${days}`),
  getAttribute: (environmentId, key) => getJson(`/admin/v1/environments/${environmentId}/catalog/attributes/${encodeURIComponent(key)}`),
  updateAttribute: (environmentId, key, patch) => request('PATCH', `/admin/v1/environments/${environmentId}/catalog/attributes/${encodeURIComponent(key)}`, patch),
  listMedia: (environmentId, filters) => {
    const query = new URLSearchParams(
      Object.entries({ ...filters, archived: filters.archived ? 'true' : undefined }).filter((entry): entry is [string, string] => typeof entry[1] === 'string' && entry[1] !== ''),
    );
    return getJson(`/admin/v1/environments/${environmentId}/media?${query}`);
  },
  getMedia: (environmentId, assetId) => getJson(`/admin/v1/environments/${environmentId}/media/${assetId}`),
  uploadMedia: (environmentId, file, name, fileName) =>
    upload(`/admin/v1/environments/${environmentId}/media?${new URLSearchParams({ name, ...(fileName ? { fileName } : {}) })}`, file),
  replaceMedia: (environmentId, assetId, file, fileName) =>
    upload(`/admin/v1/environments/${environmentId}/media/${assetId}/versions?${new URLSearchParams(fileName ? { fileName } : {})}`, file),
  updateMedia: (environmentId, assetId, patch) => request('PATCH', `/admin/v1/environments/${environmentId}/media/${assetId}`, patch),
  setMediaCrop: (environmentId, assetId, versionId, preset, crop) =>
    request('PUT', `/admin/v1/environments/${environmentId}/media/${assetId}/versions/${versionId}/renditions/${preset}`, { crop }),
  archiveMedia: (environmentId, assetId) => request('POST', `/admin/v1/environments/${environmentId}/media/${assetId}/archive`),
  restoreMedia: (environmentId, assetId) => request('POST', `/admin/v1/environments/${environmentId}/media/${assetId}/restore`),
  listTemplates: (environmentId, filters) => {
    const query = new URLSearchParams(
      Object.entries({ ...filters, archived: filters.archived ? 'true' : undefined }).filter((entry): entry is [string, string] => typeof entry[1] === 'string' && entry[1] !== ''),
    );
    return getJson(`/admin/v1/environments/${environmentId}/templates?${query}`);
  },
  getTemplate: (environmentId, templateId) => getJson(`/admin/v1/environments/${environmentId}/templates/${templateId}`),
  getTemplateVersion: (environmentId, templateId, version) => getJson(`/admin/v1/environments/${environmentId}/templates/${templateId}/versions/${version}`),
  createTemplate: (environmentId, body) => request('POST', `/admin/v1/environments/${environmentId}/templates`, body),
  updateTemplate: (environmentId, templateId, patch) => request('PATCH', `/admin/v1/environments/${environmentId}/templates/${templateId}`, patch),
  addTemplateVersion: (environmentId, templateId, body) => request('POST', `/admin/v1/environments/${environmentId}/templates/${templateId}/versions`, body),
  duplicateTemplate: (environmentId, templateId) => request('POST', `/admin/v1/environments/${environmentId}/templates/${templateId}/duplicate`, {}),
  archiveTemplate: (environmentId, templateId) => request('POST', `/admin/v1/environments/${environmentId}/templates/${templateId}/archive`),
  restoreTemplate: (environmentId, templateId) => request('POST', `/admin/v1/environments/${environmentId}/templates/${templateId}/restore`),
  instantiateTemplate: (environmentId, templateId, version) => request('POST', `/admin/v1/environments/${environmentId}/templates/${templateId}/instantiate`, version ? { version } : {}),
  listContentBlocks: (environmentId, filters) => {
    const query = new URLSearchParams();
    if (filters.query?.trim()) query.set('query', filters.query.trim());
    if (filters.archived) query.set('archived', 'true');
    return getJson(`/admin/v1/environments/${environmentId}/content-blocks?${query}`);
  },
  getContentBlock: (environmentId, blockId) => getJson(`/admin/v1/environments/${environmentId}/content-blocks/${blockId}`),
  getContentBlockVersion: (environmentId, blockId, version) => getJson(`/admin/v1/environments/${environmentId}/content-blocks/${blockId}/versions/${version}`),
  createContentBlock: (environmentId, body) => request('POST', `/admin/v1/environments/${environmentId}/content-blocks`, body),
  updateContentBlock: (environmentId, blockId, patch) => request('PATCH', `/admin/v1/environments/${environmentId}/content-blocks/${blockId}`, patch),
  addContentBlockVersion: (environmentId, blockId, body) => request('POST', `/admin/v1/environments/${environmentId}/content-blocks/${blockId}/versions`, body),
  propagateContentBlock: (environmentId, blockId, body) => request('POST', `/admin/v1/environments/${environmentId}/content-blocks/${blockId}/propagate`, body),
  archiveContentBlock: (environmentId, blockId) => request('POST', `/admin/v1/environments/${environmentId}/content-blocks/${blockId}/archive`),
  restoreContentBlock: (environmentId, blockId) => request('POST', `/admin/v1/environments/${environmentId}/content-blocks/${blockId}/restore`),
  eventPicker: async (environmentId, query) =>
    (await getJson<{ items: EventPickerItem[] }>(`/admin/v1/environments/${environmentId}/catalog/events/picker?${new URLSearchParams({ query })}`)).items,
  search: async (environmentId, q) =>
    (await getJson<{ items: SearchHit[] }>(`/admin/v1/environments/${environmentId}/search?${new URLSearchParams({ q })}`)).items,
};

/** Download link of a debug session export (BB-35); the browser sends the session cookie. */
export const privacyDownloadUrl = (environmentId: string, requestId: string) => `/admin/v1/environments/${environmentId}/privacy/requests/${requestId}/download`;
export const importErrorsUrl = (environmentId: string, importId: string) => `/admin/v1/environments/${environmentId}/imports/${importId}/errors.csv`;
export const debugExportUrl = (environmentId: string, sessionId: string, format: 'json' | 'csv') => `/admin/v1/environments/${environmentId}/debug/sessions/${sessionId}/export?format=${format}`;
